import {
  type Dispatch,
  type FormEvent,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  ChatHttpError,
  type CapabilityListingDTO,
  fetchCapabilityListing,
  resumeChat,
  resumeDeckorateChat,
  streamChat,
  streamDeckorateChat,
  type DocumentTurnContext,
  type TurnConfirmations,
} from '../services/api';
import type { Channel } from '../../shared/host';
import type {
  EmailContext,
  EmailDraft,
  EmailDraftMode,
  EmailSearchResult,
} from '../../integrations/email/types';
import { readCurrentBody, type MailboxItem } from '../../integrations/email/hooks/useMailboxItem';
import type { WordDocumentContext } from '../../../integrations/tools/word/types';
import {
  beginChangeReport,
  formatChangeLogEntries,
  formatCompletedActionsBlock,
  formatNoChangesBlock,
  formatUndoneActionsBlock,
  getWordChangeLog,
  isDocumentEditEntry,
  madeWhileTracking,
  settleTrackingStamps,
  unreportedEntries,
  verifyWordChangeLog,
  type WordChangeEntry,
} from '../../../integrations/tools/word/services/changeLog';
import { countWordText, verifyWordChange } from '../../../integrations/tools/word/services/changeVerify';
import { beginHighlightTurn } from '../../../integrations/tools/word/services/wordDocument';
import type { ChatAttachment, ChatMessage, MultipartPlan, MultipartStep, Pill, SSEEvent } from '../types';
import {
  clearChatHistory,
  loadChatHistory,
  purgeLegacyLocalChatHistory,
  saveChatHistory,
  setChatCleared,
} from '../chatHistoryStorage';
import {
  createThread,
  getThreadMessages,
  mapThreadMessage,
  resolveMultipartCursors,
  ThreadNotFoundError,
} from '../services/threadsApi';
import {
  buildSkillMessageSeed,
  forgetSkillMessageThread,
  loadSkillSession,
  lookupSkillMessageThread,
  rememberSkillMessageThread,
  saveSkillSession,
  skillMessageContextSuffix,
  type SkillSessionInfo,
} from '../skillMessageSession';
import type { SkillMessage } from '../../notifications/types';
import type { SlashCommand, SlashCommandInputRequirements } from '../slashCommands';
import { REQUEST_TOO_LARGE_MESSAGE } from '../uploadLimits';
import { DISCOVERY_SLASHES } from '../slashCommands/discovery';
import { isLocalCommand, localCommandRequest, SUMMARISE_SLASH } from '../slashCommands/localCommands';
import { usesMatchingLadder } from '../slashCommands/matching';
import { parseSlash } from '../slashCommands/parser';
import { getHardcodedBuiltInGroups, type BuiltInGroupOptions } from '../slashCommands/builtInCapabilities';
import {
  answerSlideContentRequest,
  isSlideContentRequest,
  matchShapeQuestion,
} from '../../integrations/powerpoint/deckIntelligence/slideContent';
import {
  findCommandBySkillKey,
  getCommandsForHost,
  getSkillCommandsForHost,
  loadCommandsForHost,
  lookupCommand,
} from '../slashCommands/registry';
import type { SlashContext } from '../slashCommands/types';
import { registerPillSink, registerEmailCardSink } from '../../../integrations/tools/outlook';
import { runHandler } from '../../../integrations/tools';
import { emitToast } from '../../../integrations/tools/outlook/toast-bridge';
import { PPT_WRITE_SUCCESS_TOASTS, powerPointFailureToast } from '../pills/powerpointPills';
import {
  logEvent,
  noopCapabilitySpan,
  startCapability,
  withCorrelationId,
  type CapabilityOutcome,
  type CapabilityResult,
  type CapabilitySpan,
  type CapabilityTrigger,
} from '../../telemetry';
import {
  appliedCountFor,
  capabilityForHandler,
  graphUsedFor,
  isUnsupportedApiResult,
  sectionCountFor,
  writePathFor,
} from '../capabilityMap';
import {
  createTurnEventHandler,
  createTurnState,
  dispatchPendingToolCall,
  type HandoffTarget,
  type TurnDeps,
  type TurnState,
} from './turnEvents';
import { bindReplyPillsToConversation, isStaleTargetError } from '../pills/outlookPills';
import { isUnknownHandlerError } from '../pills/pillFailures';
import { listFindings } from '../../docReview/services/api';
import { useThreadHydration } from './useThreadHydration';
import { useApprovalDecision, type ApprovalDecisionHandler } from './useApprovalDecision';
import { decideSlideRewrite, lastReplyIsDraft, rephraseSlideRequest, runRephraseSlideTurn } from './rephraseSlideTurn';
import type { SlideRewriteCard } from '../../integrations/powerpoint/features/rephrase-slide/types';
import type { SlideRewriteDecision } from '../../integrations/powerpoint/features/rephrase-slide/components/SlideRewriteCard';
import {
  buildDeckContext,
  buildDeckSummaryPrompt,
  DECK_SUMMARY_COPY,
  DECK_SUMMARY_SKILL_KEY,
  deckSummaryFailedToast,
  listSpeakerNotes,
  matchListNotesIntent,
  matchSummariseDeckIntent,
  presentationName,
  readAllSpeakerNotes,
  readDeckSlides,
  summariseSlashIntent,
  readSlideContextForTurn,
  isTextSelectionPresent,
  SPEAKER_NOTES_COPY,
  type DeckContext,
  type DeckSummaryMeta,
  type SlideTurnContext,
} from '../../integrations/powerpoint';

/** Office.js round trip for an office_action pill click. */
const PILL_TIMEOUT_MS = 30000;
/** Same, for a handler that may stop and ask the user something first. */
const CONFIRMING_PILL_TIMEOUT_MS = 5 * 60 * 1000;
/**
 * How long a carried user decision (clicked slot, approved recipients) stays
 * valid. Long enough to read a confirmation and answer it; short enough that a
 * booking the user abandoned cannot resume itself later — specification gap G-10.
 */
const CONFIRMATION_TTL_MS = 10 * 60 * 1000;

// Strips machine-readable metadata that the LLM embeds in chat_intent pill
// messages so the BACKEND can resolve a specific email/draft, but which would
// just be noise in the user-visible chat bubble. The full unredacted text is
// still sent to the API — only the rendered bubble uses the cleaned form.
//
// Removes:
//   - "(message_id=AAMk...)"
//   - "(message_ids=[AAMk..., AAMk...])"
//   - trailing whitespace + dangling punctuation left behind by the strip.
import {
  stripAgentMetadata,
  bookSlotBubbleOverride,
  dedupePills,
  parseBookSlotConfirmation,
  joinNamesNaturally,
  spliceNameSelection,
  buildResolvedContactsSuffix,
  NAME_CANDIDATE_LEAD_IN,
  type NameListAnchor,
  affirmativeOrNegative,
} from './useChatHelpers';

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data-URL prefix (e.g. "data:application/pdf;base64,")
      resolve(result.split(',')[1] ?? result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

/**
 * Synchronously resolves a chat input string against the slash-command
 * registry for the current host. Pure function — safe to export and unit-test
 * without mounting the hook.
 *
 * Returns the message body to send (slash sigil stripped; autoSendMessage
 * substituted when no args were typed) and the skill key to force, or null
 * when the input is not a recognised slash command.
 */
export function resolveSlashCommand(
  text: string,
  host: Channel | null,
  hasOpenEmail: boolean,
  hasSelectedText: boolean
): { messageBody: string; forcedSkillKey: string | null } {
  if (!text?.startsWith('/')) {
    return { messageBody: text, forcedSkillKey: null };
  }

  const spaceIdx = text.indexOf(' ');
  const token = spaceIdx === -1 ? text : text.slice(0, spaceIdx);
  const args = spaceIdx === -1 ? '' : text.slice(spaceIdx + 1).trim();

  const cmd = lookupCommand(host, token);
  if (!cmd) {
    return { messageBody: text, forcedSkillKey: null };
  }

  const ctx: SlashContext = { host: host as Channel, hasOpenEmail, hasSelectedText };

  let messageBody: string;
  if (args) {
    // AIVA-8157: the slash's OWN intent has to survive into the message.
    // Sending the args alone dropped it — "/summarise and then structure it"
    // reached word_summarise with the message "and then structure it", so the
    // model restructured the document and produced no summary at all
    // (reproduced 3/3). The description is the same natural-language proxy
    // used for a bare slash below, so a turn whose args are just PARAMETERS
    // ("/table with columns Owner, Due date") reads naturally too, and the
    // planner can now see both tasks when the args name a second one.
    messageBody = `${cmd.description}, ${args}`;
  } else if (cmd.autoSendMessage) {
    messageBody = cmd.autoSendMessage(ctx);
  } else {
    // Recognised slash with no args and no channel-specific autoSendMessage.
    // Use the backend-supplied description as a natural-language proxy
    // rather than echoing the raw "/<cmd>" — otherwise the user bubble
    // shows "/meeting" instead of "Schedule a meeting" (AIVA-5057).
    messageBody = cmd.description || text;
  }

  return { messageBody, forcedSkillKey: cmd.skillKey };
}

// Skills that only work when the user has uploaded a file in the chat (their
// slashes are always shown in the menu — see the slash endpoint's relaxed
// scoping). Invoking one with no uploaded file would 404/out-of-scope on the
// backend, so the input bar short-circuits with a "upload a file first" toast.
const UPLOAD_CONTEXTUAL_SKILL_KEYS = new Set([
  'send_attachment_draft',
  'send_attachment_compose',
  'summarize_uploaded_file',
]);

/**
 * The success toast for an Office.js write handler, or null when the handler
 * confirms itself (clipboard.copy) or is not a plain write. Exported so the
 * "every write returns a toast" contract (§9) can be unit-tested. attachment
 * drafts are handled separately (bespoke repeat-open copy), so they map to null.
 */
export function officeWriteSuccessToast(handler: string): string | null {
  return OFFICE_WRITE_SUCCESS_TOASTS[handler] ?? null;
}

// An attachment-draft "Open"/"Reply" pill. Both the frontend auto-emit (on
// create_*_draft) and the LLM's own pill_office_open_attachment_draft can fire
// in one turn; we want exactly one such button per message.
function isOpenDraftPill(p: Pill): boolean {
  return p.type === 'office_action' && p.handler === 'attachment.open_draft';
}

// Specification §9: "Every write returns a toast … no operation completes
// silently." An office_action pill click runs an Office.js write (compose /
// calendar) and renders NOTHING on success, so the user was left with no
// confirmation — the exact "no operation completes silently" gap. Map each
// write handler to the toast that confirms it. Deliberately excluded:
//   - clipboard.copy / word.copy_text — they emit their own success toast, so
//     mapping them here would double-fire.
//   - attachment.open_draft — carries bespoke repeat-open guidance (AIVA-5595),
//     handled in reportOfficeActionResult rather than from this flat map.
//   - ui.pill.* — pill-emission handlers, not writes.
// Scoped to the Outlook compose/calendar surface (this ticket's area); the
// Word write handlers keep their existing change-log / approval-card feedback.
/**
 * The signed-in user's display name, or `undefined` on a host that has none.
 *
 * Sent to the backend because the model had no way to know it: the prompt carried
 * the user's email ADDRESS and nothing else, so a draft needing a sign-off was
 * filled from whatever name was nearest — AiVA once, the open email's sender the
 * next time. Deriving one from the address is banned by spec gap G-8.
 *
 * `undefined` is a supported answer: the model is told to write no name rather
 * than invent one, which is G-7's approved closure.
 */
function signedInDisplayName(): string | undefined {
  const name = Office.context?.mailbox?.userProfile?.displayName;
  return typeof name === 'string' && name.trim() ? name.trim() : undefined;
}

/**
 * Whether a handler reported that it did NOT actually do the thing.
 *
 * `runHandler`'s `ok` flag only says the handler did not throw. Handlers that can
 * tell the difference report it — `opened: false` from the Office.js display
 * ladders, `cancelled: true` when the user declined a confirmation. Absent fields
 * mean "no opinion", which stays a success, so handlers that cannot verify
 * (`displayMessageForm` has no callback) are unaffected.
 */
function didNotComplete(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const r = data as { opened?: unknown; cancelled?: unknown };
  return r.opened === false || r.cancelled === true;
}

const OFFICE_WRITE_SUCCESS_TOASTS: Readonly<Record<string, string>> = {
  'compose.reply': 'Reply ready — review and send.',
  'compose.open': 'Draft ready — review and send.',
  'compose.insert': 'Added to your email.',
  'calendar.open': 'Opening your calendar…',
  'calendar.open_teams': 'Opening your Teams meeting…',
  // PowerPoint action bar (AIVA-6753) — the table lives with the pill specs.
  ...PPT_WRITE_SUCCESS_TOASTS,
};

/**
 * Append `incoming` pills to `existing`, dropping any that duplicate a button
 * already on the row.
 *
 * A single action can legitimately be emitted twice in one turn, because some
 * pills have BOTH a deterministic backend/frontend emitter and a model-driven
 * one that the skill prompt still asks for. Two live examples:
 *
 * - `attachment.open_draft` — frontend auto-emit on create_*_draft plus the
 *   model's pill_office_open_attachment_draft (the original reason this
 *   function existed, kept below as an id-insensitive special case).
 * - `calendar.open_teams` — submit_teams_meeting_draft's deterministic
 *   `follow_up_pill` plus the model's pill_office_calendar_open, which the
 *   tool's own `next_step` explicitly instructs it to call (AIVA-6393). That
 *   pair rendered two identical "Open Teams meeting" buttons.
 *
 * `dedupePills` keys office_action pills on handler + insert_position + label,
 * so distinct actions that happen to share a handler (Insert at cursor vs
 * Append to end, or one book-slot pill per free time) all survive.
 */
function dedupeIncomingPills(existing: Pill[], incoming: Pill[]): Pill[] {
  // Open-draft pills carry a draft_id that the two emitters can disagree on, so
  // they collapse on handler alone rather than on the shared dedupe key.
  let hasOpenDraft = existing.some(isOpenDraftPill);
  const toAdd: Pill[] = [];
  for (const p of incoming) {
    if (isOpenDraftPill(p)) {
      if (hasOpenDraft) continue;
      hasOpenDraft = true;
    }
    toAdd.push(p);
  }
  return dedupePills([...existing, ...toAdd]);
}

// Append `stamped` pills to the most recent assistant message's pill row
// (de-duplicating against what is already there). Returns `messages` unchanged
// when there is no assistant message to attach to.
function mergePillsIntoLastAssistant(messages: ChatMessage[], stamped: Pill[]): ChatMessage[] {
  const updated = [...messages];
  for (let i = updated.length - 1; i >= 0; i--) {
    const m = updated[i];
    if (m?.role !== 'assistant') continue;
    updated[i] = { ...m, actionPills: dedupeIncomingPills(m.actionPills ?? [], stamped) };
    return updated;
  }
  return messages;
}

/**
 * Pick the content rendered in the user's chat bubble.
 *
 * Priority:
 *   1. ``displayText`` override from the caller (attachment uploads etc.).
 *   2. Verbatim typed input when the user typed a recognised slash command,
 *      so the bubble shows e.g. "/meeting based on this email" rather than
 *      just the stripped args. Only fires when no ``forcedSkillKey`` was
 *      passed (palette / pill paths already supply natural-language text).
 *   3. The slash-stripped messageBody with backend-only metadata removed.
 *
 * Extracted so ``runChatTurn`` stays under SonarCloud's Cognitive Complexity cap.
 */
export function selectUserBubbleContent(
  rawText: string,
  messageBody: string,
  resolvedForcedSkillKey: string | null,
  optsForcedSkillKey: string | undefined,
  optsDisplayText: string | undefined
): string {
  if (optsDisplayText !== undefined) return optsDisplayText;
  const typedSlashRecognised =
    !optsForcedSkillKey && rawText.startsWith('/') && resolvedForcedSkillKey !== null;
  return typedSlashRecognised ? rawText.trim() : stripAgentMetadata(messageBody);
}

/**
 * How much of the sent message is kept as a rehydration pairing key. Long
 * enough to identify the turn uniquely, short enough that the legacy inline
 * attachment path (which appends an entire extracted document to the message)
 * can't blow the localStorage budget. Matching is prefix-based, so a truncated
 * key still pairs.
 */
const SENT_CONTENT_KEY_CHARS = 512;

function sentContentKey(messageBody: string): string {
  return messageBody.slice(0, SENT_CONTENT_KEY_CHARS);
}

/**
 * Whether ``runChatTurn`` should warm the slash-command registry cache
 * before evaluating the input. Fires when a token is available AND the
 * caller either passed a ``forcedSkillKey`` (pill click, palette invoke)
 * or the user typed a slash. Extracted so ``runChatTurn`` stays under
 * SonarCloud's Cognitive Complexity cap.
 */
export function shouldPreloadSlashCommands(
  apiToken: string | null,
  forcedSkillKey: string | undefined,
  text: string
): boolean {
  if (!apiToken) return false;
  return Boolean(forcedSkillKey) || text.startsWith('/');
}

/**
 * Selection-guard evaluator for slash commands that declare
 * ``inputRequirements.requiresWordSelection: true`` in Cosmos.
 *
 * Returns the nudge chat messages to post when the guard fires (skill requires
 * a selection but the Word document has none), or ``null`` when the caller
 * should proceed with the normal send. Extracted so ``runChatTurn`` stays
 * under SonarCloud's Cognitive Complexity cap.
 *
 * The user bubble is only included when ``hidden`` is falsy — pill-click
 * flows suppress it so the palette shortcut doesn't add an echoed user
 * message to history.
 */
// DISCOVERY_SLASHES lives in slashCommands/discovery.ts alongside the palette
// entries for the same four slashes — one source of truth, so the set the
// palette offers can't drift from the set this hook intercepts.
export { DISCOVERY_SLASHES };

/** Display order + heading for each slashCategory in the discovery listing. */
const DISCOVERY_GROUPS: ReadonlyArray<readonly [string, string]> = [
  ['edit', 'Editing & Content'],
  ['format', 'Formatting & Styling'],
  ['review', 'Document Review'],
  ['write', 'Templates, Outlines & Writing'],
  // The specification's sixth group (its G-5 closure): find/replace, Bates
  // numbering, page numbers, hyperlinks and paste-email were undiscoverable
  // while they had nowhere to be listed. Mirrors GROUP_ORDER in the backend's
  // capability_listing.py — that copy is authoritative, this one is the
  // offline fallback.
  ['docprod', 'Document production'],
  // PowerPoint (AIVA-6753) — same two groups as GROUP_ORDER in capability_listing.py.
  ['slide_content', 'Slide Content'],
  ['deck', 'Deck Intelligence'],
];

/** True when the typed text is one of the four discovery slashes. */
export function isDiscoverySlash(rawText: string): boolean {
  return DISCOVERY_SLASHES.has(rawText.trim().toLowerCase());
}

/**
 * SAF-1: a slash-shaped token (e.g. ``/passive``) that matches no command on
 * this channel must not be sent to the router as plain text — that's how a
 * mistyped command turns into a real instruction. Returns a refusal message
 * for that case, or null when the input isn't a command-shaped slash, is a
 * discovery slash, matches a real command, the registry hasn't loaded yet,
 * or ``forcedSkillKey`` is already set (pill click, palette pick, Regenerate
 * — the skill is a given, not ours to re-derive from the text).
 */
export function resolveUnmatchedSlashMessage(
  text: string,
  host: Channel | null,
  forcedSkillKey?: string
): string | null {
  if (forcedSkillKey) return null;
  const { token } = parseSlash(text);
  if (!token || isDiscoverySlash(token) || isLocalCommand(host, token)) return null;
  if (getSkillCommandsForHost(host).length === 0) return null;
  if (lookupCommand(host, token)) return null;
  return `I don't have a command called \`${token}\` on this channel. Type \`/?\` to see what's available, or just describe what you'd like in plain language.`;
}

const PARTIAL_MATCH_TIP = 'Type part of a command to run it. For example, `/new` runs "New slide".';
const SETTINGS_SKILLS_TIP = 'To enable or disable your skills, go to Settings → Skills.';
const PLAIN_LANGUAGE_TIP = 'You can also ask in plain language. Every command above works that way.';

/**
 * The `/?` footer (spec §4.2): partial matching, Settings → Skills and plain language.
 * Partial matching is explained only on hosts where it runs.
 */
export function discoveryFooter(channel: string | null): string {
  const partialTip = usesMatchingLadder(channel as Channel | null) ? PARTIAL_MATCH_TIP : null;
  return [partialTip, SETTINGS_SKILLS_TIP, PLAIN_LANGUAGE_TIP].filter(Boolean).join('\n\n');
}

/**
 * Render the server's grouped capability listing.
 *
 * Group titles and their order come from the backend
 * (``/api/v1/skills/capabilities/{channel}``), which projects the same
 * persona-scoped skill set the slash menu uses. Returns null for an empty
 * listing so the caller can fall back to the local render — an unauthenticated
 * or unmapped user gets a 200 with no groups, and ``/?`` must never answer with
 * nothing.
 */
export function renderCapabilityListing(
  listing: CapabilityListingDTO,
  builtInOptions: BuiltInGroupOptions = {}
): string | null {
  // The backend projects its own built-in floor for some channels (Word).
  // For a channel it doesn't yet cover (Outlook — see builtInCapabilities.ts),
  // splice in the hardcoded equivalent here. A no-op whenever the fetched
  // listing already carries a ``builtin_``-prefixed group, so this can never
  // duplicate or fight the server-owned taxonomy.
  const hasServerBuiltIns = listing.groups.some((g) => g.key.startsWith('builtin_'));
  const groups = hasServerBuiltIns
    ? listing.groups
    : [...listing.groups, ...getHardcodedBuiltInGroups(listing.channel, builtInOptions)];
  if (groups.length === 0) return null;

  // The built-in groups (server- or client-supplied) are a different KIND of
  // entry and have to read that way:
  //  - they carry an empty ``slash`` because the specification says they are
  //    "deliberately not /-firable" — rendering "/" beside them invites the
  //    user to type a command that does not exist;
  //  - they sit under their own heading, so it is obvious at a glance which
  //    entries are typed and which are asked for in plain language.
  const skillGroups = groups.filter((g) => !g.key.startsWith('builtin_'));
  const builtInGroups = groups.filter((g) => g.key.startsWith('builtin_'));

  const renderGroup = (group: CapabilityListingDTO['groups'][number], lines: string[]): void => {
    if (group.items.length === 0) return;
    lines.push('', `**${group.title}**`);
    for (const item of group.items) {
      const text = item.description || item.label;
      // No slash means "ask for this in words", so it renders as a plain bullet.
      lines.push(item.slash ? `- \`/${item.slash}\` — ${text}` : `- ${text}`);
    }
  };

  const lines: string[] = [];
  if (listing.total > 0) {
    // Wording pinned to the feature specifications' Figure 4.1 — both the
    // Word and Outlook discovery-listing mockups head the skill section with
    // this exact phrase.
    lines.push(`**Your active skills on this channel** (${listing.total})`);
  } else {
    // "(0)" above a full list reads as a failure. Say what is actually true —
    // nothing is granted to you — and let the built-in section speak for what
    // the add-in can still do.
    lines.push('**No skills are enabled for you on this channel.**');
  }
  for (const group of skillGroups) renderGroup(group, lines);

  if (builtInGroups.length > 0) {
    lines.push('', builtInHeading(builtInGroups));
    for (const group of builtInGroups) renderGroup(group, lines);
  }

  lines.push('', discoveryFooter(listing.channel));
  return lines.join('\n');
}

/**
 * Offline fallback for the ``/?`` listing, rendered from the local command
 * cache when the capabilities endpoint can't be reached.
 *
 * Answered without an LLM turn either way: this is a listing of data we already
 * hold, so a model round-trip would be slower, cost tokens and risk inventing a
 * command that doesn't exist. Returns null when the text isn't a discovery
 * slash, or when no commands are cached for the host (in which case the caller
 * falls through to a normal turn rather than showing an empty menu).
 */
/**
 * Appends one heading + bullet block per non-empty ``DISCOVERY_GROUPS``
 * category, mutating ``lines`` in place. Returns the slashes it rendered so
 * the caller can find whatever category is left over.
 */
function appendGroupedCommandLines(commands: readonly SlashCommand[], lines: string[]): Set<string> {
  const seen = new Set<string>();
  for (const [category, heading] of DISCOVERY_GROUPS) {
    const inGroup = commands.filter((c) => c.category === category);
    if (inGroup.length === 0) continue;
    lines.push('', `**${heading}**`);
    for (const c of inGroup) {
      seen.add(c.slash);
      lines.push(`- \`${c.slash}\` — ${c.description || c.label}`);
    }
  }
  return seen;
}

/**
 * Anything whose category isn't in ``DISCOVERY_GROUPS`` still has to appear —
 * a new category in Cosmos must never make a shipped command undiscoverable.
 */
function appendUncategorisedCommandLines(
  commands: readonly SlashCommand[],
  seen: ReadonlySet<string>,
  lines: string[]
): void {
  const rest = commands.filter((c) => !seen.has(c.slash));
  if (rest.length === 0) return;
  lines.push('', '**Other**');
  for (const c of rest) lines.push(`- \`${c.slash}\` — ${c.description || c.label}`);
}

/**
 * Same hardcoded floor as the online render (renderCapabilityListing) — a
 * no-op for a host with no entry in builtInCapabilities.ts.
 */
function appendBuiltInCapabilityLines(
  host: Channel | null,
  lines: string[],
  builtInOptions: BuiltInGroupOptions
): void {
  const builtInGroups = getHardcodedBuiltInGroups(host, builtInOptions);
  if (builtInGroups.length === 0) return;
  lines.push('', builtInHeading(builtInGroups));
  for (const group of builtInGroups) {
    lines.push('', `**${group.title}**`);
    for (const item of group.items) lines.push(builtInItemLine(item));
  }
}

type CapabilityGroup = CapabilityListingDTO['groups'][number];

/**
 * The heading over the built-in section. Pinned to Figure 4.1 while every entry
 * is asked for in words; once a built-in carries its slash command (AIVA-8971)
 * the heading says both work.
 */
function builtInHeading(groups: readonly CapabilityGroup[]): string {
  const hasCommands = groups.some((g) => g.items.some((item) => item.slash));
  return hasCommands
    ? '**Built-in capabilities · type the command or ask in natural language**'
    : '**Built-in capabilities · ask in natural language**';
}

/** A built-in's line, with its command when it has one — the same shape as a skill's line. */
function builtInItemLine(item: CapabilityGroup['items'][number]): string {
  const text = item.description || item.label;
  return item.slash ? `- \`/${item.slash}\` — ${text}` : `- ${text}`;
}

/** AIVA-8971: the built-ins are listed with the commands this user can run. */
function withAvailableSlashes(host: Channel | null, options: BuiltInGroupOptions): BuiltInGroupOptions {
  return { ...options, availableSlashes: new Set(getCommandsForHost(host).map((c) => c.slash)) };
}

export function buildDiscoveryListing(
  host: Channel | null,
  rawText: string,
  builtInOptions: BuiltInGroupOptions = {}
): string | null {
  if (!isDiscoverySlash(rawText)) return null;
  // Skill commands only: the discovery slashes are in the palette registry, and
  // listing them here would have /? advertise itself.
  const commands = getSkillCommandsForHost(host);
  // AIVA-6804: a channel with no slash skills (PowerPoint has none) still has
  // its built-in section, so it renders that rather than returning null — null
  // sent the literal "/?" to the model, and the specification forbids an empty
  // help response. Null only when there is truly nothing to list.
  if (commands.length === 0 && getHardcodedBuiltInGroups(host, builtInOptions).length === 0) return null;

  // Same header wording as the online render (renderCapabilityListing),
  // pinned to the feature specifications' Figure 4.1.
  const lines: string[] = [
    commands.length > 0
      ? `**Your active skills on this channel** (${commands.length})`
      : '**No skills are enabled for you on this channel.**',
  ];
  const seen = appendGroupedCommandLines(commands, lines);
  appendUncategorisedCommandLines(commands, seen, lines);
  appendBuiltInCapabilityLines(host, lines, builtInOptions);

  lines.push('', discoveryFooter(host));
  return lines.join('\n');
}

/**
 * Resolve the ``/?`` listing: server taxonomy first, local cache as fallback.
 *
 * Extracted from runChatTurn so the fetch, the empty-listing case and the
 * offline case are all handled in one place rather than inline.
 */
async function resolveDiscoveryListing(args: {
  rawText: string;
  host: Channel | null;
  apiToken: string | null;
  refreshTokens: (() => Promise<string | null>) | null;
  builtInOptions: BuiltInGroupOptions;
}): Promise<string | null> {
  const { rawText, host, apiToken, refreshTokens } = args;
  if (!isDiscoverySlash(rawText)) return null;
  const builtInOptions = withAvailableSlashes(host, args.builtInOptions);
  if (host) {
    try {
      const listing = await fetchCapabilityListing(host, apiToken, refreshTokens ?? undefined);
      const rendered = renderCapabilityListing(listing, builtInOptions);
      if (rendered) return rendered;
    } catch {
      // Endpoint unreachable — fall through to the local cache below.
    }
  }
  return buildDiscoveryListing(host, rawText, builtInOptions);
}

/**
 * Everything runChatTurn can answer locally, without starting an LLM turn:
 * the discovery slashes first, then SAF-1's unmatched-slash refusal. Both
 * share the same "post one assistant bubble and stop" handling at the call
 * site, so they're resolved together here — extracted from runChatTurn to
 * keep it under Sonar's Cognitive Complexity cap (S3776).
 */
async function resolveLocalTranscriptAnswer(args: {
  text: string;
  host: Channel | null;
  activeHost: Channel;
  apiToken: string | null;
  refreshTokens: (() => Promise<string | null>) | null;
  forcedSkillKey: string | undefined;
  builtInOptions: BuiltInGroupOptions;
}): Promise<string | null> {
  const { text, host, activeHost, apiToken, refreshTokens, forcedSkillKey, builtInOptions } = args;
  const discoveryListing = await resolveDiscoveryListing({
    rawText: text,
    host,
    apiToken,
    refreshTokens,
    builtInOptions,
  });
  if (discoveryListing) return discoveryListing;
  return resolveUnmatchedSlashMessage(text, activeHost, forcedSkillKey);
}

/**
 * DI-02 (AIVA-6756) — "list the speaker notes" is answered locally from the
 * open presentation, with no AI call, so it also works when the AI service is
 * offline (spec ST-03). Posts "Reading speaker notes…", walks the deck, then
 * swaps that bubble for the notes card in place.
 *
 * Returns false when the prompt isn't a PowerPoint notes request, so the
 * caller carries on with the normal turn.
 */
export async function runLocalSpeakerNotesTurn(args: {
  text: string;
  host: Channel | null;
  hidden: boolean | undefined;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  nextMsgId: () => string;
  readNotes?: typeof readAllSpeakerNotes;
}): Promise<boolean> {
  const { text, host, hidden, setMessages, nextMsgId, readNotes = readAllSpeakerNotes } = args;
  if (host !== 'powerpoint' || !matchListNotesIntent(text)) return false;

  const now = Date.now();
  const assistantId = nextMsgId();
  setMessages((prev) => [
    ...prev,
    ...(hidden ? [] : [{ id: nextMsgId(), role: 'user' as const, content: text.trim(), timestamp: now }]),
    { id: assistantId, role: 'assistant' as const, content: SPEAKER_NOTES_COPY.READING, timestamp: now },
  ]);

  const result = await listSpeakerNotes(readNotes);
  setMessages((prev) =>
    prev.map((m) => (m.id === assistantId ? { ...m, content: '', speakerNotesList: result } : m)),
  );
  return true;
}

/**
 * True when this turn is a local PowerPoint Deck Intelligence request (DI-04)
 * that must be answered from Office.js without an LLM turn. A forced skill
 * (palette / pill / slash) always wins: the user asked for that skill.
 */
export function isLocalDeckIntelligenceTurn(
  host: Channel | null,
  text: string,
  forcedSkillKey: string | undefined
): boolean {
  if (host !== 'powerpoint' || forcedSkillKey) return false;
  // Shape questions are answered from the slide too; the AI sometimes guessed without reading it.
  return isSlideContentRequest(text) || matchShapeQuestion(text) !== null;
}

/**
 * The user + assistant bubbles for a turn answered locally. A hidden turn
 * (programmatic send) posts the answer only.
 */
export function localExchangeMessages(
  text: string,
  answer: string,
  hidden: boolean | undefined,
  timestamp: number,
  nextId: () => string
): ChatMessage[] {
  const user: ChatMessage[] = hidden ? [] : [{ id: nextId(), role: 'user', content: text.trim(), timestamp }];
  return [...user, { id: nextId(), role: 'assistant', content: answer, timestamp }];
}

/** What a DI-01 summary turn carries into runChatTurn's second pass. */
export interface DeckSummaryTurn {
  presentationName: string;
  context: DeckContext;
  meta: DeckSummaryMeta;
}

/** The re-send that asks the model for the summary, with the deck attached. */
export interface DeckSummaryResend {
  message: string;
  opts: {
    displayText: string;
    forcedSkillKey: string;
    hidden: boolean | undefined;
    deckSummary: DeckSummaryTurn;
    /** The local "Reading deck…" bubbles, swapped for the real turn in one update. */
    replacesMessageIds: readonly string[];
  };
}

/**
 * DI-01 (AIVA-6811) — posts "Reading deck…", then reads the deck before any AI call.
 * An empty or unreadable deck is answered in place (returns null); otherwise returns the re-send.
 */
export async function prepareDeckSummaryTurn(args: {
  text: string;
  bulletCount: number;
  hidden: boolean | undefined;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  nextMsgId: () => string;
  readSlides?: typeof readDeckSlides;
}): Promise<DeckSummaryResend | null> {
  const { text, bulletCount, hidden, setMessages, nextMsgId, readSlides = readDeckSlides } = args;

  const now = Date.now();
  const userId = nextMsgId();
  const statusId = nextMsgId();
  setMessages((prev) => [
    ...prev,
    ...(hidden ? [] : [{ id: userId, role: 'user' as const, content: text.trim(), timestamp: now }]),
    { id: statusId, role: 'assistant' as const, content: DECK_SUMMARY_COPY.READING, timestamp: now },
  ]);

  const slides = await readSlides();
  const read = slides === null ? ({ kind: 'unreadable' } as const) : buildDeckContext(slides);
  if (read.kind !== 'ok') {
    const content = read.kind === 'empty' ? DECK_SUMMARY_COPY.EMPTY_DECK : DECK_SUMMARY_COPY.UNREADABLE;
    setMessages((prev) => prev.map((m) => (m.id === statusId ? { ...m, content } : m)));
    return null;
  }

  const { context } = read;
  return {
    message: buildDeckSummaryPrompt(bulletCount, context),
    opts: {
      displayText: text.trim(),
      forcedSkillKey: DECK_SUMMARY_SKILL_KEY,
      hidden,
      deckSummary: {
        presentationName: presentationName(typeof Office === 'undefined' ? undefined : Office.context?.document?.url),
        context,
        meta: {
          bulletCount,
          slidesSummarised: context.slidesIncluded,
          totalSlides: context.totalSlides,
          truncated: context.truncated,
        },
      },
      replacesMessageIds: hidden ? [statusId] : [userId, statusId],
    },
  };
}

/**
 * A PowerPoint "summarise this deck" prompt, or `/summarise [N bullets]`, on its first pass;
 * the re-send is never intercepted.
 */
function deckSummaryIntent(
  text: string,
  host: Channel,
  deckSummary: DeckSummaryTurn | undefined
): ReturnType<typeof matchSummariseDeckIntent> {
  if (host !== 'powerpoint' || deckSummary) return null;
  const { token, args } = parseSlash(text);
  if (token === SUMMARISE_SLASH) return summariseSlashIntent(args);
  return matchSummariseDeckIntent(text);
}

/** Reads the deck, then re-sends the turn with it. Holds `sendingRef` only for the read. */
async function runDeckSummary(args: {
  text: string;
  bulletCount: number;
  hidden: boolean | undefined;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  nextMsgId: () => string;
  sendingRef: MutableRefObject<boolean>;
  resend: HandoffResend | null;
}): Promise<void> {
  const { sendingRef, resend, ...prepare } = args;
  sendingRef.current = true;
  let next: DeckSummaryResend | null;
  try {
    next = await prepareDeckSummaryTurn(prepare);
  } finally {
    sendingRef.current = false;
  }
  if (next) await resend?.(next.message, next.opts);
}

/** The whole deck in the document envelope, marked `scope: "deck"` so it isn't read as the current slide. */
export function deckSummaryDocumentContext(deck: DeckSummaryTurn): DocumentTurnContext {
  const { text, totalSlides } = deck.context;
  return {
    name: deck.presentationName,
    text,
    wordCount: text.split(/\s+/).filter(Boolean).length,
    charCount: text.length,
    hasSelection: false,
    slideCount: totalSlides,
    scope: 'deck',
  };
}

/** PowerPoint's document envelope for a turn: the whole deck on a summary turn, else the current slide. */
function powerPointDocumentContext(
  deck: DeckSummaryTurn | undefined,
  slide: SlideTurnContext | null
): DocumentTurnContext | null {
  return deck ? deckSummaryDocumentContext(deck) : slide;
}

/** AC-5: a failed summary also says so in a toast. `null` is a user stop, not a failure. */
function toastDeckSummaryFailure(reason: string | null): void {
  if (reason) emitToast({ title: deckSummaryFailedToast(reason), intent: 'error' });
}

/** Adds the AC-5 toast for a mid-stream `error` event; thrown errors are caught in runChatTurn. */
function withDeckSummaryFailureToast(
  handleEvent: (event: SSEEvent) => void,
  deckSummary: DeckSummaryTurn | undefined
): (event: SSEEvent) => void {
  if (!deckSummary) return handleEvent;
  return (event) => {
    handleEvent(event);
    if (event.event === 'error') {
      toastDeckSummaryFailure(event.data.message ?? 'Something went wrong. Please try again.');
    }
  };
}

/** Fallback when a Microsoft 365-required skill declares no nudge text of its own. */
const GENERIC_M365_NUDGE =
  "I can't reach your Microsoft 365 mailbox. Please sign in again, then try that once more.";

/**
 * The input requirements of the command this turn runs.
 *
 * AIVA-8971: one skill can serve several commands with different requirements
 * — ppt_draft_content is both /draft and /rewrite, and only /rewrite needs a
 * selected shape — so the command the user typed decides. A turn with no typed
 * command (a pill, the palette, plain language) keeps the skill's first
 * command, as before.
 */
export function turnInputRequirements(
  host: Channel | null,
  forcedSkillKey: string,
  rawText = ''
): SlashCommandInputRequirements | undefined {
  const { token } = parseSlash(rawText);
  const typed = token ? lookupCommand(host, token) : null;
  const command = typed?.skillKey === forcedSkillKey ? typed : findCommandBySkillKey(host, forcedSkillKey);
  return command?.inputRequirements;
}

/** Fallback when a selection-required skill declares no nudge text of its own. */
const GENERIC_SELECTION_NUDGE = 'Please select some text first.';

/**
 * Resolve the nudge text for a slash command that declares
 * ``inputRequirements.requiresWordSelection: true`` in Cosmos.
 *
 * Returns the message to surface when the guard fires (skill needs a selection
 * but the Word document has none), or ``null`` when the caller should proceed
 * with the normal send.
 *
 * Deliberately returns text rather than chat messages: the guard is reported as
 * a toast only. An unanswered command has nothing to say in the transcript, and
 * posting a question-and-answer pair the user never asked for left two bubbles
 * of clutter behind every mis-fired slash.
 */
export function resolveSelectionGuardNudge(args: {
  forcedSkillKey: string | undefined;
  host: Channel | null;
  hasSelection: boolean;
  rawText?: string;
}): string | null {
  if (!args.forcedSkillKey) return null;
  const requirements = turnInputRequirements(args.host, args.forcedSkillKey, args.rawText);
  if (!requirements?.requiresWordSelection) return null;
  if (args.hasSelection) return null;
  return requirements.nudgeIfMissing ?? GENERIC_SELECTION_NUDGE;
}

const GENERIC_SUBJECT_NUDGE = 'Tell me what to write, or select the text to draft next to.';

/**
 * Resolve the nudge for a slash command that needs a subject or a selection.
 *
 * AIVA-7937. `/write` alone, on a document with nothing selected, gives the
 * model no topic and no location — so it invents both, and two unconstrained
 * choices cannot produce the same answer twice. The turn is refused here rather
 * than sent, because the inconsistency is in the REQUEST and no prompt rule
 * fixes an underspecified one.
 *
 * A subject is any word the user typed after the slash. `/write draft a
 * termination clause` passes with nothing selected; a bare `/write` passes with
 * a selection, which supplies both the topic and the place. Only both-missing
 * is refused, so the flows that work today are untouched.
 */
export function resolveSubjectGuardNudge(args: {
  forcedSkillKey: string | undefined;
  host: Channel | null;
  hasSelection: boolean;
  rawText: string;
}): string | null {
  if (!args.forcedSkillKey) return null;
  const requirements = turnInputRequirements(args.host, args.forcedSkillKey, args.rawText);
  if (!requirements?.requiresSubjectOrSelection) return null;
  if (args.hasSelection) return null;
  // Everything after the slash token. A command typed with no argument leaves
  // nothing behind once the slash itself is removed.
  const subject = args.rawText.trim().replace(/^\/\S+/, '').trim();
  if (subject.length > 0) return null;
  return requirements.nudgeIfMissing ?? GENERIC_SUBJECT_NUDGE;
}

const GENERIC_SLIDE_SELECTION_NUDGE = 'Select a shape with text in PowerPoint first.';

/**
 * Resolve the nudge for a PowerPoint command that works on the selected shape's
 * text (AIVA-8971, `/rewrite`): refused until a shape with text is selected,
 * so the model is never asked to rewrite a selection that isn't there.
 *
 * The slide is read only when the command declares the need, so every other
 * turn pays nothing. `isTextSelected` is the same read that enables the action
 * bar's Replace selection, so the two always agree.
 */
export async function resolveSlideSelectionGuardNudge(args: {
  forcedSkillKey: string | undefined;
  host: Channel | null;
  rawText: string;
  isTextSelected: () => Promise<boolean>;
}): Promise<string | null> {
  if (!args.forcedSkillKey || args.host !== 'powerpoint') return null;
  const requirements = turnInputRequirements(args.host, args.forcedSkillKey, args.rawText);
  if (!requirements?.requiresSlideTextSelection) return null;
  if (await args.isTextSelected()) return null;
  return requirements.nudgeIfMissing ?? GENERIC_SLIDE_SELECTION_NUDGE;
}

/** The selection a regenerate is replaying, or `undefined` on an ordinary turn. */
type ReplaySelection = { readonly text: string; readonly hasSelection: boolean } | undefined;

/**
 * AIVA-7571 — the selection the guard should judge, replay first.
 *
 * On a regenerate the LIVE selection has usually just been cleared BY the
 * Regenerate click, so reading it would nudge the user about a turn that
 * demonstrably had a selection — the guard is what let it run in the first
 * place, and the partial reply on screen proves it.
 */
export function guardSelectionState(replay: ReplaySelection, wordDoc: WordDocumentContext | null): boolean {
  return replay?.hasSelection ?? Boolean(wordDoc?.hasSelection);
}

/**
 * AIVA-7571 — freeze the live selection onto the message being sent.
 *
 * Frozen alongside `skillKey` and for the same reason: a regenerate has to know
 * what the turn RAN with, not what is selected now. Returns a spreadable
 * fragment so a turn with no selection adds no key at all — its absence is what
 * keeps AIVA-7079's deselected-retry guard firing for turns that genuinely had
 * nothing selected.
 */
export function frozenSelectionFragment(
  wordDoc: WordDocumentContext | null
): { sentSelection?: { readonly text: string; readonly hasSelection: boolean } } {
  if (!wordDoc?.hasSelection) return {};
  return { sentSelection: { text: wordDoc.text, hasSelection: true } };
}

/**
 * AIVA-7571 — the document context a replayed turn should be answered against.
 *
 * Fixing only the guard would let the retry through with no selection, silently
 * widening a selection-scoped skill to the whole document — the outcome
 * AIVA-7079 existed to prevent. Returns *wordDoc* untouched when this is not a
 * replay.
 */
export function applyReplaySelection(
  wordDoc: WordDocumentContext | null,
  replay: ReplaySelection
): WordDocumentContext | null {
  if (!replay || !wordDoc) return wordDoc;
  return {
    ...wordDoc,
    text: replay.text,
    hasSelection: replay.hasSelection,
    charCount: replay.text.length,
  };
}

/**
 * The Word context a turn should run against: the send-time re-read when a
 * resolver is wired (Word host), the cached hook state otherwise (non-Word
 * hosts, and older callers/tests that pass no resolver). Extracted from
 * runChatTurn to keep it under Sonar's cognitive-complexity cap (S3776).
 */
async function resolveLiveWordDoc(
  host: Channel,
  wordDoc: WordDocumentContext | null,
  resolveSendWordDoc: (() => Promise<WordDocumentContext>) | null
): Promise<WordDocumentContext | null> {
  if (host !== 'word' || !resolveSendWordDoc) return wordDoc;
  return resolveSendWordDoc();
}

/**
 * TON-1: merges a fresh, live-read compose body into `mailboxItem` at send
 * time — typing into an open compose window never fires `ItemChanged`, so
 * the cached body can still be empty (or stale) by the time the user asks
 * AiVA to act on it. Scoped to the body only: subject/recipients are what
 * the banner already confirms are correct, and a read item's state only
 * changes on an `ItemChanged` this hook already hears.
 */
async function resolveLiveMailboxItem(host: Channel, mailboxItem: MailboxItem): Promise<MailboxItem> {
  if (host !== 'outlook' || !mailboxItem.isCompose) return mailboxItem;
  return { ...mailboxItem, body: await readCurrentBody() };
}

/**
 * Refuse a Microsoft 365-dependent command while Graph is unreachable.
 *
 * Runs before the send so no AI call is made in place of the operation: mailbox
 * search and meeting booking cannot work without Graph, and letting the turn run
 * spends a router classification and an agent run to reach a certain failure.
 * Only skills declaring ``inputRequirements.requiresMicrosoft365`` are gated, so
 * the compose and tone paths — which are pure Office.js — keep working.
 *
 * Toast-only, like the selection guard: the turn never ran, so it has nothing to
 * say in the transcript.
 */
export function resolveM365GuardNudge(args: {
  forcedSkillKey: string | undefined;
  host: Channel | null;
  m365Connected: boolean;
  rawText?: string;
}): string | null {
  if (!args.forcedSkillKey) return null;
  if (args.m365Connected) return null;
  const requirements = turnInputRequirements(args.host, args.forcedSkillKey, args.rawText);
  if (!requirements?.requiresMicrosoft365) return null;
  return requirements.nudgeIfMissing ?? GENERIC_M365_NUDGE;
}

/**
 * Decide whether this turn must start a fresh server thread, plus the email id
 * and selection fingerprint to record as "last seen". Extracted from
 * runChatTurn (like the guards above) to keep its cognitive complexity under
 * the Sonar cap (S3776).
 *
 * Fresh-thread cases:
 *   (a) the Word selection CHANGED since the last turn — the checkpoint from a
 *       previous slash command must not bleed into the next selection
 *       (AIVA-7880), or
 *   (b) the open Outlook email changed since the last turn — a previous email's
 *       summary/context must not bleed into this one (the LLM otherwise
 *       sometimes replays an earlier email's summary instead of grounding on
 *       the current email).
 *
 * Both are the same rule: a fresh thread when the THING BEING WORKED ON
 * changes, not on every turn that happens to have one. Same-email and
 * same-selection follow-ups keep the thread, so "expand on your earlier
 * summary", "now in 3 bullets" and "redo again" all still work. Skipped on the
 * very first turn (last id null) so a restored session isn't discarded.
 *
 * AIVA-8357 — this used to fire on the mere PRESENCE of a selection, and on any
 * `word_`-prefixed slash command, so every Word turn started its own thread.
 * A follow-up therefore reached the server with no history: the router had no
 * previous `[skill: ...]` to continue, so "redo again" after /improve fell
 * through to `word_general`, which is barred from synthesising placement pills
 * (see wordFallbackIsAuthorised) — hence a re-run that lost its Insert/Replace
 * buttons. Comparing against the previous selection keeps AIVA-7880's actual
 * intent, which its own docstring states as "must not bleed into the NEXT
 * selection", while letting a follow-up on the SAME selection continue.
 */
/**
 * Identify WHICH text is selected, for resolveThreadReset's selection
 * comparison — `null` when nothing is selected, since a whole-document turn has
 * no selection to compare and must not read as "the selection changed".
 *
 * Extracted rather than inlined at the call site purely to keep runChatTurn's
 * cognitive complexity under the Sonar cap (S3776), the same reason
 * resolveThreadReset itself lives out here.
 */
export function selectionFingerprint(wordDoc: WordDocumentContext | null | undefined): string | null {
  if (!wordDoc?.hasSelection) return null;
  return wordDoc.text ?? '';
}

export function resolveThreadReset(args: {
  forcedSkillKey: string | undefined;
  host: Channel | null;
  hasSelection: boolean;
  itemId: string | null | undefined;
  lastEmailId: string | null;
  /**
   * Identifies WHICH text is selected, not merely that something is. Undefined
   * on a build that cannot report it — then this falls back to the old
   * presence test, so an older add-in keeps today's behaviour rather than
   * silently gaining cross-selection bleed.
   */
  selectionKey?: string | null;
  lastSelectionKey?: string | null;
}): {
  currentEmailId: string | null;
  currentSelectionKey: string | null;
  shouldStartFreshThread: boolean;
} {
  const isWord = args.host === 'word';
  const currentSelectionKey = isWord ? (args.selectionKey ?? null) : null;

  // No selection fingerprint available: keep the pre-AIVA-8357 behaviour rather
  // than assume the selection is unchanged.
  const selectionUnknown = isWord && args.hasSelection && args.selectionKey === undefined;

  const selectionSwitched =
    currentSelectionKey !== null &&
    args.lastSelectionKey !== null &&
    args.lastSelectionKey !== undefined &&
    currentSelectionKey !== args.lastSelectionKey;

  const isWordSelectionTurn = isWord && (selectionUnknown || selectionSwitched);

  const currentEmailId = args.host === 'outlook' ? (args.itemId ?? null) : null;
  const emailSwitched =
    currentEmailId !== null && args.lastEmailId !== null && currentEmailId !== args.lastEmailId;

  return {
    currentEmailId,
    currentSelectionKey,
    shouldStartFreshThread: isWordSelectionTurn || emailSwitched,
  };
}

/**
 * The attachments payload a request should carry (AIVA-6902/6903/6904).
 *
 * `sessionFiles` is the stored session list, whose entries carry the real
 * `uploaded_this_turn` flag (restamped each turn by `prepareAttachmentsToSend`).
 * Both the initial send and the approval resume go through here, so both describe
 * the same files with the same flags — which is the point: the backend's file
 * selector attaches an unnamed file only when that flag is true.
 *
 * The resume used to stamp `uploaded_this_turn: true` on EVERY session file, on the
 * reasoning that the flag was moot because the approved tool call carries its own
 * `file_numbers`. That holds only when `file_numbers` is present, and it is absent
 * for the common "attach this" case where the backend falls back to the this-turn
 * files. Marking everything this-turn re-presented the whole session history as
 * freshly uploaded — the very thing this ticket is about. Reading the stored flag
 * fixes that without the opposite failure (dropping a file the user did just
 * upload), which a flag-less payload would have caused.
 *
 * Returns `undefined` rather than `[]` for an empty list: the request field is
 * optional, and the tools' own no-uploads precondition is what reports the absence.
 */
export function resumeAttachments(sessionFiles: ChatAttachment[]): ChatAttachment[] | undefined {
  return sessionFiles.length ? sessionFiles : undefined;
}

/**
 * Restamp `uploaded_this_turn` on the stored session files (AIVA-6902/6903/6904).
 *
 * `thisTurnContents` holds the base64 of the files that arrived on THIS request, so
 * every other stored file becomes `false`. The backend's file selector attaches an
 * unnamed file only when the flag is true, which is what scopes an attachment to the
 * turn it was uploaded on.
 *
 * `keepExistingFlags` exists for one case: a multipart plan STEP. A step is a
 * continuation of the turn that uploaded the file, not a fresh turn — it re-sends no
 * files at all (see handleMultipartStep) and says so with `attachmentKnownPresent`.
 * Restamping there would clear the flag the originating turn earned, and the
 * fail-closed selector would then refuse the step's draft with "which file did you
 * mean?" — a file the user had already attached and confirmed. The old fail-OPEN
 * default ("no selection -> every session file") hid this by attaching everything.
 */
export function stampUploadFlags(
  sessionFiles: ChatAttachment[],
  thisTurnContents: Set<string>,
  keepExistingFlags: boolean
): ChatAttachment[] {
  if (keepExistingFlags) return sessionFiles;
  return sessionFiles.map((f) => ({
    ...f,
    uploaded_this_turn: thisTurnContents.has(f.content_base64),
  }));
}

export interface UseChatReturn {
  messages: ChatMessage[];
  input: string;
  setInput: (value: string) => void;
  streaming: boolean;
  activeTool: string | null;
  /** Sanitised backend progress line (v2 `activity` events); null when idle. */
  activityLine: string | null;
  /** AIVA-8194: rule keys the latest doc-review scan was scoped to; null for
   *  an unscoped scan or before any scan has run. */
  docReviewScope: string[] | null;
  /** Approve/Deny click on an in-chat approval card (v2 HITL). */
  handleApprovalDecision: ApprovalDecisionHandler;
  pendingDraft: EmailDraft | null;
  clearDraft: () => void;
  clearChat: () => void;
  chatEndRef: React.RefObject<HTMLDivElement>;
  startNewChat: () => void;
  stopStreaming: () => void;
  regenerate: (assistantMessageId: string) => void;
  handleSend: (e: FormEvent, attachedContent?: string, attachedFileName?: string) => void;
  handlePillClick: (pill: Pill) => Promise<void>;
  /** AIVA-6436 — Amend feedback on a Word write-pill approval card. */
  handlePillAmend: (pill: Pill, feedback: string) => Promise<void>;
  /** Run / skip / cancel a step of a sequential-multipart plan card. */
  handleMultipartStep: (
    messageId: string,
    action: 'run' | 'skip' | 'cancel',
    step?: MultipartStep
  ) => Promise<void>;
  handlePillBooked: (messageId: string, pillLabel: string) => void;
  /** SC-06 — Apply rewrites / Cancel on a slide rewrite confirm card. */
  handleSlideRewrite: (messageId: string, card: SlideRewriteCard, decision: SlideRewriteDecision) => Promise<void>;
  /** Ids of the currently-selected name-candidate (disambiguation) pills —
   *  drives each pill's toggled visual state in ActionPills. */
  selectedNameCandidateIds: ReadonlySet<string>;
  runSlashCommand: (cmd: SlashCommand) => Promise<void>;
  /** ST-05 — send `text` as a palette-triggered turn, as if typed. */
  sendPrompt: (text: string) => Promise<void>;
  attachedFiles: File[];
  addAttachedFiles: (files: File[]) => void;
  removeAttachedFile: (index: number) => void;
  /** Distinct files retained for the whole session (across turns). */
  sessionUploadedFileCount: number;
  /** Raw bytes of the retained session files — drives the input bar's
   *  cumulative attach-time budget (AIVA-7072). */
  sessionUploadedFileBytes: number;
  /** The live server-side thread id (AIVA-5929), null before the first send
   *  of a fresh conversation. A ref, not state, so reading it does not
   *  itself trigger a re-render — callers that need it read `.current` at
   *  their own render time (e.g. AIVA-7506's Doc Review panel, which is
   *  shown well after the thread that ran /check-references exists). */
  threadIdRef: MutableRefObject<string | null>;
  /** True once word.docreview.anchor_findings has dispatched this session
   *  (AIVA-7506) — the signal that the inline doc-review card (chat tab)
   *  has findings worth fetching. A ref for the same reason as threadIdRef:
   *  read at the caller's own render time rather than triggering one itself. */
  docReviewReadyRef: MutableRefObject<boolean>;
  /** The skill message whose chat session is on screen; null for an ordinary chat. */
  skillSession: SkillSessionInfo | null;
  /** Open a skill message as its own chat session (see skillMessageSession.ts). */
  openSkillMessage: (message: SkillMessage) => void;
  /** Leave the skill message's session and restore the conversation it replaced. */
  returnToPreviousChat: () => Promise<void>;
}

/**
 * Run a chat/resume call with one silent token-refresh on 401.
 *
 * Extracted from the in-closure version inside ``runChatTurn`` to keep the
 * latter under SonarCloud's Cognitive Complexity cap. On the first 401 we
 * call ``refreshTokens`` once; if MSAL succeeds we retry the call with the
 * new bearer and return it so the caller can hand the same token to
 * subsequent resume calls in the same turn without waiting for the
 * AuthState context to re-render. Any other failure (or a second 401)
 * propagates so the caller's catch can map it to a specific user message.
 */
async function runWithAuthRetry(
  op: (token: string | null) => Promise<void>,
  initialToken: string | null,
  refreshTokens: (() => Promise<string | null>) | null
): Promise<string | null> {
  try {
    await op(initialToken);
    return initialToken;
  } catch (err) {
    if (err instanceof ChatHttpError && err.status === 401 && refreshTokens) {
      const fresh = await refreshTokens();
      if (fresh) {
        await op(fresh);
        return fresh;
      }
    }
    throw err;
  }
}

/**
 * AIVA-5861 pre-flight: resolve the bearer to send for a turn. Returns the
 * pre-flight fresh token when a refresher is supplied (so a token we already
 * know is expired is never sent), falling back to the current ``apiToken``.
 * Extracted from ``runChatTurn`` to keep that function under SonarCloud's
 * Cognitive Complexity cap (the inline ternary pushed it over).
 */
async function resolveTurnBearer(
  ensureFreshToken: (() => Promise<string | null>) | null,
  apiToken: string | null
): Promise<string | null> {
  const fresh = ensureFreshToken ? await ensureFreshToken() : null;
  return fresh ?? apiToken;
}

/**
 * Map an HTTP status from a backend chat/resume call to its user-facing
 * message.  Extracted from ``classifyChatError`` to keep that function under
 * SonarCloud's Cognitive Complexity cap (each nested status branch counts
 * twice — once for the ``if`` and once for the nesting level).
 *
 * 401 is reached only after the silent token refresh has already failed; a
 * successful refresh retries transparently inside ``useChat`` and never
 * reaches this mapper.
 */
function statusCodeToMessage(status: number): string {
  if (status === 401) return 'Your sign-in has expired. Please Sign in to continue.';
  if (status === 403) return "AiVA isn't available for your account. Please contact your administrator.";
  if (status === 429) return 'AiVA is busy right now. Please wait a moment and try again.';
  // 413 = the REQUEST was too large (message + context + the whole retained
  // attachment set, base64-encoded) — not any single file, and there is no
  // per-file "10 MB" limit on any layer. Copy lives with the limits it
  // describes (AIVA-7072). With the attach-time budget in ChatInputBar this
  // path should now be unreachable, but the gateway keeps the final say.
  if (status === 413) return REQUEST_TOO_LARGE_MESSAGE;
  if (status === 0) return "Can't reach AiVA. Check your network connection and try again.";
  if (status >= 500) return 'AiVA is temporarily unavailable. Please try again in a moment.';
  return `AiVA couldn't process this request (error ${status}). Please try again.`;
}

/**
 * Maps a thrown error from streamChat / resumeChat / the dispatch loop to a
 * user-facing message that says what actually happened and (when relevant)
 * what action the user should take next.  Returning `null` means "treat as a
 * silent intentional abort — don't show any error bubble".
 */
function classifyChatError(err: unknown): string | null {
  // User-initiated abort (panel unmount, new message, /stop). DOMException
  // name "AbortError" or our abort signal triggered: stay silent.
  if (err instanceof DOMException && err.name === 'AbortError') return null;
  if (err instanceof Error && err.name === 'AbortError') return null;

  if (err instanceof ChatHttpError) return statusCodeToMessage(err.status);

  // `fetch` itself rejected (network down, DNS failure, CORS, TLS) — surfaces
  // as TypeError in the browser. Distinct from HTTP-level errors above.
  if (err instanceof TypeError) {
    return "Can't reach AiVA. Check your network connection and try again.";
  }

  return 'Something went wrong. Please try again.';
}

function handleStreamError(
  err: unknown,
  assistantMsgId: string,
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
  wasUserStopped = false
): void {
  const friendly = classifyChatError(err);
  if (friendly === null) {
    if (wasUserStopped) {
      setMessages((prev) => prev.map((m) => (m.id === assistantMsgId ? { ...m, stopped: true } : m)));
    } else {
      setMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
    }
  } else {
    setMessages((prev) => {
      const updated = [...prev];
      updated[updated.length - 1] = { id: assistantMsgId, role: 'assistant', content: friendly };
      return updated;
    });
  }
}

/**
 * Build the EmailContext payload sent with /chat. Extracted so ``runChatTurn``
 * doesn't carry the ternary + property fan-out against its Cognitive
 * Complexity budget. Returns ``null`` when no email is open (subject empty),
 * which the route handler treats as "no email context for this turn".
 */
function buildEmailContext(mailboxItem: MailboxItem): EmailContext | null {
  if (!mailboxItem.subject) return null;
  return {
    subject: mailboxItem.subject,
    normalizedSubject: mailboxItem.normalizedSubject,
    from: mailboxItem.from,
    sender: mailboxItem.sender,
    to: mailboxItem.to,
    cc: mailboxItem.cc,
    body: mailboxItem.body,
    dateReceived: mailboxItem.dateReceived,
    dateModified: mailboxItem.dateModified,
    importance: mailboxItem.importance,
    attachments: mailboxItem.attachments,
    itemId: mailboxItem.itemId,
    conversationId: mailboxItem.conversationId,
    internetMessageId: mailboxItem.internetMessageId,
    itemType: mailboxItem.itemType,
  };
}

/**
 * Ceiling api.ts applies to document_context.text (``text.slice(0, 6000)``).
 * The /changes turn budgets its appended change-log block against this so the
 * log is never truncated away by that slice on long documents.
 */
const MAX_DOC_CONTEXT_CHARS = 6000;

/**
 * The tracking command whose turn carries the full change report.
 *
 * ``/stop-tracking``, not ``/start-tracking`` (AIVA-8040). Reporting on start was
 * backwards: at that moment nothing has been tracked yet, so the report could
 * only ever describe a PREVIOUS session's leftovers. The changes are what the
 * user wants when they finish reviewing and switch tracking off.
 *
 * Named once because two call sites read it — the memory block below and
 * ``buildDocumentContext``'s whole-document rule — and a swap that updated only
 * one would send the report with a selection-narrowed document.
 */
const WORD_CHANGE_REPORT_SKILL_KEY = 'word_track_stop';

export async function buildCompletedActionsMemory(
  host: Channel | null,
  forcedSkillKey?: string
): Promise<string> {
  if (host !== 'word') return '';
  if (forcedSkillKey === WORD_CHANGE_REPORT_SKILL_KEY) return buildChangeReport();
  if (getWordChangeLog().length === 0) return '';
  // countWordText: AIVA-8046 — text edits are counted, not just found, so an
  // undone edit can't be vouched for by an identical later one.
  const { present, undone } = await verifyWordChangeLog(verifyWordChange, countWordText);
  if (present.length === 0 && undone.length === 0) return '';
  return formatCompletedActionsBlock(present) + formatUndoneActionsBlock(undone);
}

/**
 * The /stop-tracking report's change blocks. Three rules decide what is in it:
 *
 * - AIVA-8048: only edits made WHILE Track Changes was on — an edit made
 *   after Stop Tracking was reported by the next Stop Tracking as tracked;
 * - AIVA-8048: only what no earlier report showed — the second Stop Tracking
 *   re-listed the first one's Replace and Insert at cursor edits. Begun even
 *   when the log is empty, so this turn's word_get_review_items is still
 *   measured against the previous report;
 * - AIVA-8046: an undone edit is counted out, not vouched for by an identical
 *   later one (countWordText).
 *
 * With nothing left, it still sends an explicit "none" block: with no block at
 * all the model reported its own "turned Track Changes off" as a change.
 */
async function buildChangeReport(): Promise<string> {
  beginChangeReport();
  await settleTrackingStamps();
  const { present, undone } =
    getWordChangeLog().length === 0
      ? { present: [], undone: [] }
      : await verifyWordChangeLog(verifyWordChange, countWordText);
  // Toggles are session state, not edits — see isDocumentEditEntry.
  // The report turn carries BOTH halves. Keeping only `present` dropped the
  // undone entries on the floor, and the model — asked to describe what
  // changed — answered from the transcript, where AIVA had said it applied
  // them. So a Ctrl+Z'd replace-selection or insert-at-cursor was still
  // reported as a change. The negative block is the only thing that
  // contradicts the transcript, so it has to travel with this turn too.
  const reportable = (list: readonly WordChangeEntry[]) =>
    unreportedEntries(list.filter(isDocumentEditEntry).filter(madeWhileTracking));
  const applied = reportable(present);
  const removed = reportable(undone);
  if (applied.length === 0 && removed.length === 0) return formatNoChangesBlock();
  return formatChangeLogEntries(applied) + formatUndoneActionsBlock(removed);
}

/**
 * Build the WordDocumentContext payload sent with /chat. Only included when the
 * host is Word AND the doc has content — avoids upload cost for empty docs or
 * Outlook turns. Extracted from ``runChatTurn`` to keep the latter's Cognitive
 * Complexity under the SonarCloud cap.
 *
 * ``memoryBlock`` is the verified completed-actions memory from
 * {@link buildCompletedActionsMemory} (already checked against the live
 * document), appended to the document text so it travels with the turn. A
 * /stop-tracking turn (word_track_stop) carries the full change report and is
 * reported over the whole document (never narrowed to a selection); every other
 * turn carries the compact memory and only augments a document that has content.
 * The verification is done by the async caller, so this stays synchronous —
 * ``getResumeContext`` calls it on a hot, non-async path.
 */
/**
 * SC-01 (AIVA-6809) — the slide context for a PowerPoint turn, or null.
 *
 * General route only: a Deckorate turn goes to the DeckorateChat endpoint,
 * which does not take a document context. Exported for tests.
 */
export async function slideContextForRoute(route: ChatRoute): Promise<SlideTurnContext | null> {
  return route === 'general' ? await readSlideContextForTurn() : null;
}

export function buildDocumentContext(
  host: Channel | null,
  wordDoc: WordDocumentContext | null,
  opts: { forcedSkillKey?: string; memoryBlock?: string } = {}
): WordDocumentContext | null {
  if (host !== 'word') return null;
  const { forcedSkillKey, memoryBlock } = opts;
  const isReport = forcedSkillKey === WORD_CHANGE_REPORT_SKILL_KEY;

  if (memoryBlock) {
    const body = (wordDoc?.text ?? '').slice(0, Math.max(0, MAX_DOC_CONTEXT_CHARS - memoryBlock.length));
    const text = body + memoryBlock;
    return {
      name: wordDoc?.name ?? 'Untitled Document',
      text,
      charCount: text.length,
      wordCount: text.split(/\s+/).filter(Boolean).length,
      // The change report covers the whole document; never narrow it to a
      // selection. Ordinary turns keep the document's real selection state.
      hasSelection: isReport ? false : (wordDoc?.hasSelection ?? false),
    };
  }

  if (!wordDoc || wordDoc.wordCount <= 0) return null;
  // A report turn with no log block still reports over the whole document
  // (AIVA-8040). The whole-document rule used to be reachable only through the
  // memoryBlock branch, so a session whose only logged action was toggling
  // tracking — now filtered out of the block — fell through here and sent the
  // document narrowed to the user's selection.
  return isReport ? { ...wordDoc, hasSelection: false } : wordDoc;
}

/**
 * Hydrate the slash registry if needed, then resolve the turn's message body and
 * forced skill key.
 *
 * The registry must be hydrated before resolving: ChatTabPanel's useEffect fires
 * loadCommandsForHost on mount, but it races a fast typer — if the cache is
 * still empty on Send, lookupCommand misses and the raw "/cmd args" leaks into
 * the user bubble (AIVA-5057). Idempotent and cheap on a cache hit. It also runs
 * when a forcedSkillKey is passed (e.g. a pill click) so downstream lookups like
 * findCommandBySkillKey see a hydrated cache.
 *
 * An explicit forcedSkillKey (palette / pill-driven turn) wins and skips
 * re-parsing. The slash sigil itself never reaches the LLM — skill prompts are
 * tuned for natural-language entry.
 *
 * Extracted from runChatTurn to keep it under Sonar's complexity cap.
 */
async function resolveTurnSkill(args: {
  text: string;
  forcedSkillKey: string | undefined;
  host: Channel;
  apiToken: string | null;
  refreshTokens: (() => Promise<string | null>) | null;
  hasSubject: boolean;
  hasSelection: boolean;
}): Promise<{ messageBody: string; forcedSkillKey: string | null }> {
  const { text, forcedSkillKey, host, apiToken, refreshTokens } = args;
  if (shouldPreloadSlashCommands(apiToken, forcedSkillKey, text)) {
    await loadCommandsForHost(host, apiToken, refreshTokens ?? undefined).catch(() => {
      // Network unavailable — fall through to natural-language routing.
    });
  }
  if (forcedSkillKey) return { messageBody: text, forcedSkillKey };
  return resolveSlashCommand(text, host, args.hasSubject, args.hasSelection);
}

/**
 * Reason the current turn cannot be sent (shown as an assistant bubble), or null
 * when it may proceed. Extracted so runChatTurn stays under Sonar's
 * cognitive-complexity cap by collapsing two guards into one.
 */
function sendBlockedMessage(host: Channel | null, aiProviderConnected: boolean): string | null {
  if (host === null) {
    return "AiVA can't run here — please open this add-in inside Outlook, Word, or PowerPoint.";
  }
  // AIVA-6350: the OpenAI/LLM connector is disconnected in Studio. The add-in
  // stays usable (history, tabs, settings) but can't answer NL queries.
  if (!aiProviderConnected) {
    return "AiVA can't answer right now — the AI provider is disconnected. Ask your AiVA Studio administrator to reconnect the OpenAI connector, then try again.";
  }
  return null;
}

type OfficeActionPill = Extract<Pill, { type: 'office_action' }>;

/**
 * Attachment-draft (Open/Reply) pills open a one-shot Graph draft. Once the
 * user has sent it the id no longer resolves and the open is a silent no-op,
 * so on a repeat click nudge them toward their Sent folder; a first open is
 * confirmed like any other write (§9).
 *
 * Extracted from `handlePillClick` to keep it under SonarCloud's Cognitive
 * Complexity cap.
 */
function reportAttachmentDraftOpen(p: OfficeActionPill, openedDraftIds: Set<string>): void {
  const draftId = typeof p.args?.draft_id === 'string' ? p.args.draft_id : '';
  const reopened = !!draftId && openedDraftIds.has(draftId);
  if (draftId) openedDraftIds.add(draftId);
  if (reopened) {
    emitToast({
      title:
        p.label === 'Reply'
          ? 'You have already opened this reply. If you sent it, find it in your Sent folder.'
          : 'You have already opened this draft. If you sent it, find it in your Sent folder.',
      intent: 'info',
    });
  } else {
    emitToast({ title: 'Draft ready — review and send.', intent: 'success' });
  }
}

/**
 * Report the outcome of an office_action pill's handler call: an error toast
 * naming what to do next (§9), an in-place assistant message when the
 * handler took a fallback path worth surfacing, an open-draft nudge, or a
 * plain success toast.
 *
 * A toast, not a chat message, for the error case — Specification §9:
 * "Every write returns a toast" — the transcript is for AiVA's replies, and
 * a failed click is transient operation feedback. As a message it also got
 * PERSISTED to the thread store and rehydrated on reopening the pane,
 * turning one mis-click into permanent conversation history. The most
 * common cause is clicking a pill from an earlier turn after switching email
 * or surface: the handler refuses rather than writing into the wrong
 * thread, so the copy names that instead of repeating the raw handler error
 * — which is for developers (see the console.warn below), and names
 * registry keys, host flags and Office.js internals.
 *
 * Extracted from `handlePillClick` to keep it under SonarCloud's Cognitive
 * Complexity cap.
 */
function reportOfficeActionResult(
  p: OfficeActionPill,
  result: Awaited<ReturnType<typeof runHandler>>,
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
  nextMsgId: () => string,
  openedDraftIds: Set<string>
): void {
  if (!result.ok) {
    console.warn('[useChat] office_action pill failed:', p.handler, result.error);
    let title: string;
    const pptTitle = powerPointFailureToast(result.error);
    if (pptTitle) {
      // A PowerPoint write that failed for a reason the user can fix — e.g. no
      // slide selected in Slide Sorter view (T-SC02-5). Say what to do.
      title = pptTitle;
    } else if (isStaleTargetError(result.error)) {
      title = 'That action was for a different email. Open that message again and retry from there.';
    } else if (isUnknownHandlerError(result.error)) {
      title = 'That button is out of date — the add-in has been updated since. Ask again to get a fresh one.';
    } else {
      title = "Couldn't do that. Please try again.";
    }
    emitToast({ title, intent: 'error' });
    return;
  }
  // Handler ran but took a fallback path worth surfacing (e.g. calendar.open
  // copied details to clipboard instead of opening the appointment form). The
  // posted message IS the in-place result, so it stands in for the toast —
  // §9: "status lines are replaced in place by their result".
  if (result.data && typeof (result.data as { message?: string }).message === 'string') {
    setMessages((prev) => [
      ...prev,
      {
        id: nextMsgId(),
        role: 'assistant',
        content: (result.data as { message: string }).message,
        timestamp: Date.now(),
      },
    ]);
    return;
  }
  // §9 "Every write returns a toast … no operation completes silently." An
  // Office.js write renders nothing on success, so confirm it here.
  if (p.handler === 'attachment.open_draft') {
    reportAttachmentDraftOpen(p, openedDraftIds);
    return;
  }
  // A toast saying "ready — review and send" over a window that never opened is
  // worse than silence: it sends the user looking for something that is not
  // there. `result.ok` only means the handler did not throw.
  if (didNotComplete(result.data)) return;
  // A handler may return its own wording, e.g. Insert into slide placing text beside a full slide.
  const handlerToast = (result.data as { toast?: unknown } | undefined)?.toast;
  const successTitle = typeof handlerToast === 'string' ? handlerToast : officeWriteSuccessToast(p.handler);
  if (successTitle) emitToast({ title: successTitle, intent: 'success' });
}

/**
 * Dispatch an office_action pill to its Office.js handler and report the
 * outcome. A handler that can raise a confirmation dialog is bounded by how
 * long the USER takes to answer, not by how long an Office.js call takes —
 * the calendar Graph fallback (AIVA-6985) asks about external recipients
 * mid-handler; at the standard 30s a user who paused to read it would see
 * "Couldn't do that" while the booking went ahead behind the toast.
 *
 * Extracted from `handlePillClick` to keep it under SonarCloud's Cognitive
 * Complexity cap.
 */
async function dispatchOfficeActionPill(
  p: OfficeActionPill,
  host: Channel | null,
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
  nextMsgId: () => string,
  openedDraftIds: Set<string>,
  // For pill handlers that call the API themselves, e.g. word.insert_email.
  auth: { apiToken: string | null; refresh?: () => Promise<string | null> }
): Promise<void> {
  if (host === null) {
    console.error('[useChat] cannot dispatch pill — no Office host detected');
    return;
  }
  // A click runs on the pill's OWN args — nothing is borrowed from live state.
  //
  // Compose pills used to have `latestDraft`'s subject / to / cc / draft_id
  // grafted on here. `latestDraft` is the session's most recent draft, cleared
  // only by New Chat, so it reached buttons on later turns that had no draft of
  // their own. `composeReply` short-circuits on `draft_id`, so *Draft reply*
  // under an /actions or /summarise answer opened an earlier email's draft and
  // dropped the answer it was meant to place (AIVA-8588; spec TF-01 AC-3,
  // MS-02). The turn's own draft is bound into its buttons when they are built —
  // `draftArgs` in chat/pills/outlookPills.ts — and the conversation they are
  // for is bound in the pill sink (`bindReplyPillsToConversation`).
  const pillArgs = p.args;
  const timeoutMs = p.handler.startsWith('calendar.open') ? CONFIRMING_PILL_TIMEOUT_MS : PILL_TIMEOUT_MS;

  const capabilityId = capabilityForHandler(host, p.handler, pillArgs);
  const span = capabilityId
    ? startCapability({
        capabilityId,
        trigger: 'action_button',
        surface: outlookSurface(host),
      })
    : noopCapabilitySpan();

  try {
    const result = await runHandler(host, p.handler, pillArgs, timeoutMs, auth);
    span.complete(officeActionOutcome(host, result, pillArgs));
    reportOfficeActionResult(p, result, setMessages, nextMsgId, openedDraftIds);
  } finally {
    span.complete({ outcome: 'error', errorCode: 'pill_dispatch_failed' });
  }
}

function resolveTurnOutcome(
  turnState: TurnState,
  turnFailed: boolean,
  userStopped: boolean
): CapabilityResult {
  const aiUsed = true;
  if (userStopped) return { outcome: 'cancelled', aiUsed };
  if (turnState.guardrailFlagged) return { outcome: 'not_connected', aiUsed };
  if (turnFailed) return { outcome: 'error', errorCode: 'chat_stream_failed', aiUsed };
  if (turnState.committable === false) return { outcome: 'no_match', aiUsed };
  return { outcome: 'success', aiUsed };
}

/**
 * Close out the turn's capability span in `runChatTurn`'s finally block.
 *
 * A turn that paused for approval keeps its span open on purpose:
 * `useApprovalDecision` owns it from there and completes it with the user's
 * decision, so completing it here would report the run as finished while the
 * card is still on screen.
 */
function completeTurnCapabilitySpan(
  spanRef: MutableRefObject<CapabilitySpan | null>,
  turnState: TurnState,
  turnFailed: boolean,
  userStopped: boolean
): void {
  if (!spanRef.current || turnState.approvalRequested) return;
  spanRef.current.complete(resolveTurnOutcome(turnState, turnFailed, userStopped));
  spanRef.current = null;
}

function outlookSurface(host: Channel | null): 'read' | 'compose' | 'none' | undefined {
  if (host !== 'outlook') return undefined;
  const item = Office.context?.mailbox?.item;
  if (!item) return 'none';
  const body = (item as { body?: { setSelectedDataAsync?: unknown } }).body;
  return typeof body?.setSelectedDataAsync === 'function' ? 'compose' : 'read';
}

function officeActionErrorCode(error: unknown): string {
  if (isStaleTargetError(error)) return 'stale_target';
  if (isUnknownHandlerError(error)) return 'unknown_handler';
  return 'office_action_failed';
}

function officeActionSuccessOutcome(unsupported: boolean, completed: boolean): CapabilityOutcome {
  if (unsupported) return 'unsupported_api';
  return completed ? 'success' : 'cancelled';
}

function officeActionOutcome(
  host: Channel | null,
  result: Awaited<ReturnType<typeof runHandler>>,
  args: Record<string, unknown>
): CapabilityResult {
  if (!result.ok) {
    return {
      outcome: 'error',
      errorCode: officeActionErrorCode(result.error),
    };
  }

  const data = result.data;
  const completed = !didNotComplete(data);
  const writePath = writePathFor(data, args);
  const graphUsed = graphUsedFor(data);
  const unsupported = isUnsupportedApiResult(data);
  const facts: CapabilityResult = {
    outcome: officeActionSuccessOutcome(unsupported, completed),
    ...(writePath ? { writePath } : {}),
    ...(graphUsed !== undefined ? { graphUsed } : {}),
    aiUsed: false,
  };

  if (host === 'word') {
    facts.wroteToDocument = completed && !unsupported;
    const applied = appliedCountFor(data);
    if (applied !== undefined) facts.appliedCount = applied;
    const sections = sectionCountFor(data, args);
    if (sections !== undefined) facts.sectionCount = sections;
  } else {
    facts.committed = completed;
  }
  return facts;
}

type NameCandidatePill = Extract<Pill, { type: 'name_candidate' }>;

/**
 * Toggle one name-candidate pill in/out of the current multi-select, and
 * splice the chat input's name-list portion to match the new selection.
 *
 * On the very first pick into a genuinely empty box, seeds the sentence the
 * user would otherwise have had to start typing themselves — never when they
 * already have text (typed, or a prior selection's leftovers), which
 * `spliceNameSelection`'s anchor already protects further down. Gated on
 * `skillKey` because find_user_by_name fires this same pill on several
 * non-meeting skills too (email compose/reply, send_attachment_compose,
 * email_search) — seeding "Set up meeting with" on a reply or cc turn would
 * just be wrong.
 *
 * Mutates `nameListAnchorRef`/`lastJoinedNameTextRef` in place — the same
 * pattern `dispatchOfficeActionPill` above uses for `openedDraftIds` — rather
 * than threading them back out through the return value.
 *
 * Extracted from `handlePillClick` to keep it under SonarCloud's Cognitive
 * Complexity cap.
 */
function applyNameCandidateClick(
  pill: NameCandidatePill,
  selected: Map<string, { name: string; mail?: string }>,
  input: string,
  nameListAnchorRef: MutableRefObject<NameListAnchor | null>,
  lastJoinedNameTextRef: MutableRefObject<string>
): { text: string; selected: Map<string, { name: string; mail?: string }> } {
  const wasEmpty = selected.size === 0;
  const next = new Map(selected);
  if (next.has(pill.id)) {
    next.delete(pill.id);
  } else {
    next.set(pill.id, { name: pill.name, mail: pill.mail });
  }
  // Read BEFORE the resolved-attendee seeding below, which adds entries and
  // would otherwise push `next.size` past 1 and lose the lead-in on exactly the
  // turn that needs it.
  const isFirstPickIntoEmptyBox = wasEmpty && next.size === 1 && input.trim() === '';
  // Attendees the same turn resolved uniquely never became candidates, so there
  // is no pill for the user to click and nothing to put them back into the
  // sentence. Seed them alongside the first pick, once, so a request naming
  // several people survives one of them being ambiguous — otherwise picking one
  // candidate rewrites the whole request as "Set up meeting with <that
  // candidate>" and the already-resolved attendee is silently dropped.
  //
  // Keyed by address so they can never collide with a candidate's id (its name),
  // and only on that first pick: after it the sentence is the user's, and
  // re-adding a name they deliberately deleted would fight them.
  if (isFirstPickIntoEmptyBox) {
    for (const entry of pill.alreadyResolved ?? []) {
      const key = `resolved|${entry.mail ?? entry.name}`;
      if (!next.has(key)) next.set(key, { name: entry.name, mail: entry.mail });
    }
  }
  const newJoined = joinNamesNaturally([...next.values()].map((v) => v.name));
  const base =
    isFirstPickIntoEmptyBox && pill.skillKey === 'calendar_assistant' ? NAME_CANDIDATE_LEAD_IN : input;
  // Computed synchronously off `input` rather than via setInput's
  // functional-updater form: that form only runs the updater once React
  // flushes the queued state change, so mutating nameListAnchorRef inside it
  // raced a second rapid click, which read the anchor before it had been
  // written and fell back to appending a duplicate name list.
  const spliced = spliceNameSelection(
    base,
    nameListAnchorRef.current,
    lastJoinedNameTextRef.current,
    newJoined
  );
  nameListAnchorRef.current = spliced.anchor;
  lastJoinedNameTextRef.current = newJoined;
  return { text: spliced.text, selected: next };
}

/**
 * Freezes the name-candidate selection onto the assistant message that
 * produced it, so its pills stay checked forever (matching what was
 * actually sent) even after the live selection is cleared for the next
 * turn. Extracted out of runChatTurn's setMessages updater to keep its
 * cognitive complexity under the Sonar limit (S3776).
 */
function freezeNameCandidateSelection(prev: ChatMessage[], ids: string[]): ChatMessage[] {
  for (let i = prev.length - 1; i >= 0; i--) {
    const m = prev[i]!;
    if (
      m.role === 'assistant' &&
      m.actionPills?.some((p) => p.type === 'name_candidate' && ids.includes(p.id))
    ) {
      const updated = [...prev];
      updated[i] = { ...m, lockedNameCandidateIds: ids };
      return updated;
    }
  }
  return prev;
}

/**
 * Marks any not-yet-decided approval as stale — a fresh turn abandons it
 * server-side (agent_base._sanitise_orphan_tool_messages stubs out the old
 * gated tool call). Extracted out of runChatTurn to keep its cognitive
 * complexity under the Sonar limit (S3776).
 */
function staleUndecidedApprovals(prev: ChatMessage[]): ChatMessage[] {
  return prev.map((m) =>
    m.approval && m.approval.decision === undefined
      ? { ...m, approval: { ...m.approval, stale: true } }
      : m
  );
}

/**
 * Resolve the message text sent when files are attached but the user typed
 * nothing worth appending. Extracted from `sendChatMessage`'s nested
 * ternary, flagged directly by SonarCloud (typescript:S3358).
 */
function buildEffectiveText(text: string, resolvedContactsSuffix: string, hasFiles: boolean): string {
  if (text) return `${text}${resolvedContactsSuffix}`;
  if (hasFiles) return 'Please analyse the attached file.';
  return '';
}

/**
 * Build and send the actual chat turn for `handleSend`, branching on whether
 * the caller pre-extracted attachment text (the legacy path) or is sending
 * newly attached files with no extracted text yet.
 *
 * Extracted from `handleSend`'s correlation-scoped callback to keep it under
 * SonarCloud's Cognitive Complexity cap — the two branches plus their
 * nested ternaries were the largest contributor.
 */
type ChatTurnRunner = (
  text: string,
  opts: {
    displayText?: string;
    attachedFileNames?: string[];
    files?: File[];
    capabilityTrigger?: CapabilityTrigger;
  }
) => Promise<void>;

interface SendChatMessageParams {
  text: string;
  resolvedContactsSuffix: string;
  attachedContent: string | undefined;
  attachedFileName: string | undefined;
  filesToSend: File[];
  capabilityTrigger?: CapabilityTrigger;
}

/** Legacy text-based attachment path: the caller already extracted the file's
 *  text, so it is inlined into the message body. Split out of
 *  {@link sendChatMessage} to keep both paths under Sonar's cap. */
async function sendPreExtractedAttachmentTurn(
  runChatTurn: ChatTurnRunner,
  params: SendChatMessageParams,
  fileNames: string[]
): Promise<void> {
  const { text, resolvedContactsSuffix, attachedContent, attachedFileName, filesToSend, capabilityTrigger } =
    params;
  const textWithContacts = text ? `${text}${resolvedContactsSuffix}` : text;
  const messageText = textWithContacts
    ? `${textWithContacts}\n\n[Attached file]\n${attachedContent}`
    : `[Attached file]\n${attachedContent}`;
  const fileNamesAll = [...(attachedFileName ? [attachedFileName] : []), ...fileNames];
  await runChatTurn(messageText, {
    displayText: text || ' ',
    attachedFileNames: fileNamesAll.length > 0 ? fileNamesAll : undefined,
    files: filesToSend,
    ...(capabilityTrigger ? { capabilityTrigger } : {}),
  });
}

/** Ordinary path: no pre-extracted text, so any files ride along as uploads. */
async function sendPlainTurn(
  runChatTurn: ChatTurnRunner,
  params: SendChatMessageParams,
  fileNames: string[]
): Promise<void> {
  const { text, resolvedContactsSuffix, filesToSend, capabilityTrigger } = params;
  // When files are attached but the user typed no message, supply a minimal
  // prompt so the backend has something to act on. Only set displayText in
  // this case so the bubble shows nothing (just the file badge) instead of
  // the auto-generated prompt.
  const hasFiles = fileNames.length > 0;
  const effectiveText = buildEffectiveText(text, resolvedContactsSuffix, hasFiles);
  await runChatTurn(effectiveText, {
    // Only override the bubble when there's actually hidden metadata to hide
    // — otherwise leave displayText unset so slash-command expansion etc.
    // still resolve the bubble exactly as before.
    ...(!text && hasFiles ? { displayText: '' } : {}),
    ...(text && resolvedContactsSuffix ? { displayText: text } : {}),
    attachedFileNames: hasFiles ? fileNames : undefined,
    files: filesToSend,
    ...(capabilityTrigger ? { capabilityTrigger } : {}),
  });
}

async function sendChatMessage(
  runChatTurn: ChatTurnRunner,
  params: SendChatMessageParams
): Promise<void> {
  const fileNames = params.filesToSend.map((f) => f.name);
  if (params.attachedContent) {
    await sendPreExtractedAttachmentTurn(runChatTurn, params, fileNames);
    return;
  }
  await sendPlainTurn(runChatTurn, params, fileNames);
}

/** Which backend a chat turn is sent to. Only PowerPoint ever takes the
 *  Deckorate route; every other host is always `general`. */
export type ChatRoute = 'general' | 'deckorate';

/**
 * Picks the backend for one turn. PowerPoint used to send every turn to
 * DeckorateChat (`isDeckorate = host === 'powerpoint'`), which left the general
 * PowerPoint skills on /api/v2/chat unreachable. Now a turn goes to Deckorate
 * only when the Deckorate module is enabled for the persona AND the turn asks
 * for it: an explicit `route: 'deckorate'` from a Deckorate entry point, or a
 * forced `deckorate_*` skill (a Deckorate slash command or skill pill).
 * Everything typed in the composer is `general`.
 */
export function resolveChatRoute(
  host: Channel | null,
  deckorateEnabled: boolean,
  requested: ChatRoute | undefined,
  forcedSkillKey: string | undefined
): ChatRoute {
  if (host !== 'powerpoint' || !deckorateEnabled) return 'general';
  if (requested === 'deckorate') return 'deckorate';
  if (forcedSkillKey?.startsWith('deckorate_')) return 'deckorate';
  return 'general';
}

/** AIVA-8792 — the slice of runChatTurn the Deckorate re-send needs. */
type HandoffResend = (
  text: string,
  opts:
    | {
        hidden: true;
        route: ChatRoute;
        handoffOf: { readonly userMsgId: string; readonly assistantMsgId: string };
      }
    // DI-01 — the summary pass, once the deck has been read.
    | DeckSummaryResend['opts']
) => Promise<void>;

/**
 * AIVA-8792 — whether a finished turn must be re-sent to the DeckorateChat
 * endpoint. True only when v2 answered with a `handoff` to Deckorate, the pass
 * actually went to v2, and it is not itself a re-send — so a turn is handed off
 * at most once and can never bounce between the two endpoints.
 */
export function shouldHandOffToDeckorate(
  handoffTarget: HandoffTarget | null,
  wentToDeckorate: boolean,
  handoffOf: unknown
): boolean {
  return handoffTarget === 'deckorate' && !wentToDeckorate && !handoffOf;
}

/**
 * AIVA-9184 — whether v2 handed the turn back to run as the slide rewrite
 * (SC-06), which only the add-in can run. Never from a re-send, so a turn is
 * handed off at most once.
 */
export function shouldHandOffToRephrase(
  handoffTarget: HandoffTarget | null,
  wentToDeckorate: boolean,
  handoffOf: unknown
): boolean {
  return handoffTarget === 'rephrase_slide' && !wentToDeckorate && !handoffOf;
}

/**
 * The history a turn sends. For the Deckorate re-send of a handed-off turn
 * (AIVA-8792), the first pass's user bubble and its (now removed) empty
 * assistant bubble are left out: the message itself is sent as the turn, so
 * including its bubble would hand Deckorate the same request twice.
 */
export function historyForTurn<T extends { readonly id: string }>(
  messages: readonly T[],
  handoffOf: { readonly userMsgId: string; readonly assistantMsgId: string } | undefined
): T[] {
  if (!handoffOf) return [...messages];
  return messages.filter((m) => m.id !== handoffOf.userMsgId && m.id !== handoffOf.assistantMsgId);
}

/** Optional, lower-frequency inputs for {@link useChat}, bundled so the hook
 *  stays within Sonar's 7-parameter limit. */
interface UseChatExtras {
  /**
   * Whether the Deckorate module is enabled for this persona and not switched
   * off in Settings > Modules — the same check that shows the Deckorate tile.
   * Read when a turn starts. Absent (older callers/tests, Outlook, Word) means
   * disabled, so every turn takes the general route.
   */
  isDeckorateEnabled?: (() => boolean) | null;
  /** Studio display name of the signed-in user's persona (used in the trace name). */
  personaDisplayName?: string | null;
  /**
   * AIVA-6350: false when the AI provider is disconnected in Studio — the add-in
   * stays visible but must not answer natural-language queries. Defaults true so
   * older callers/tests keep sending.
   */
  aiProviderConnected?: boolean;
  /**
   * False when Microsoft 365 is unreachable. Gates the mailbox/calendar skills
   * that declare requiresMicrosoft365. Defaults true so older callers/tests send.
   */
  m365Connected?: boolean;
  /**
   * AIVA-5861 pre-flight: resolves to a backend bearer guaranteed fresh past the
   * refresh buffer, refreshing first when the tracked expiry says it is stale, so
   * a turn never sends a token we already know is expired. Supplied by
   * AuthStateProvider; when absent (older callers/tests) the send falls back to
   * the current `apiToken` and the reactive 401 retry.
   */
  ensureFreshToken?: (() => Promise<string | null>) | null;
  /**
   * Re-reads the live Word selection and document body when a turn starts, so
   * the context a turn sends is a fresh fact rather than the event-driven cache
   * `wordDoc` holds. Word does not reliably fire DocumentSelectionChanged on a
   * deselect, so without this a slash command run after the user clicked away
   * still answered over the previous selection. When absent (older
   * callers/tests) the turn uses `wordDoc` exactly as before.
   */
  resolveSendWordDoc?: (() => Promise<WordDocumentContext>) | null;
}

export function useChat(
  apiToken: string | null,
  mailboxItem: MailboxItem,
  wordDoc: WordDocumentContext | null,
  host: Channel | null,
  disclaimerText: string | null = null,
  refreshTokens: (() => Promise<string | null>) | null = null,
  extras: UseChatExtras = {}
): UseChatReturn {
  // Lower-frequency inputs are bundled into `extras` so the hook stays within
  // Sonar's 7-parameter cap (AIVA-6350 added aiProviderConnected).
  const {
    personaDisplayName = null,
    aiProviderConnected = true,
    m365Connected = true,
    ensureFreshToken = null,
    resolveSendWordDoc = null,
    isDeckorateEnabled = null,
  } = extras;
  // AIVA-5796: Word/PowerPoint chat history must be isolated per document, so
  // opening a different document starts a fresh conversation instead of showing
  // the previous document's chat. The document URL is a stable per-document id
  // and becomes part of the localStorage key. Outlook is intentionally excluded
  // (contextId stays null → the legacy host-only bucket): closing and reopening
  // the side panel on the same email must keep its chat (AIVA-5171), and
  // switching emails already resets the backend thread via the emailSwitched
  // guard in runChatTurn. Null (Outlook, or an unsaved document with no URL)
  // falls back to that host-only bucket.
  const contextId = useMemo<string | null>(() => {
    if (host !== 'word' && host !== 'powerpoint') return null;
    try {
      return Office.context.document?.url || null;
    } catch {
      return null;
    }
  }, [host]);

  // AIVA-5171: restore any conversation persisted by a previous mount so the
  // history survives closing and reopening the side panel. Read once on mount —
  // the host and (for Word/PowerPoint) the open document are fixed for the task
  // pane's lifetime, since opening a different document mounts the pane afresh.
  const restoredRef = useRef(loadChatHistory(host, contextId));
  const msgCounterRef = useRef(restoredRef.current?.msgCounter ?? 0);
  const sendingRef = useRef(false);
  // AIVA-8792 — runChatTurn re-sends a handed-off turn to Deckorate through
  // this ref rather than calling itself, so it always uses the latest closure
  // without listing itself as its own useCallback dependency.
  const runChatTurnRef = useRef<HandoffResend | null>(null);
  // SC-06 — open telemetry spans for slide rewrite cards, by card message id.
  const slideRewriteSpansRef = useRef(new Map<string, CapabilitySpan>());
  const pillsEmittedRef = useRef(false);
  // Whether list_emails_obo returned at least one email this turn — lets the
  // empty-bubble fallback tell catchup's "no unread mail" case apart from the
  // "has results but the model streamed no prose" case (both show as an empty
  // bubble + a refine pill on the Conversational-tier model).
  const listEmailsHadResultsRef = useRef(false);
  // The open email's itemId as of the previous turn. When the user switches
  // emails we reset the backend conversation thread (fresh sessionId + no
  // history) so a summary/context from the PREVIOUS email can't bleed into the
  // new one — the LLM otherwise sometimes replays an earlier email's summary
  // instead of grounding on the current email. Same-email turns keep the thread,
  // so "expand on your earlier summary" / "now in 3 bullets" still work.
  const lastEmailIdRef = useRef<string | null>(null);
  // AIVA-8357: the Word counterpart of lastEmailIdRef — the text of the
  // selection the previous turn ran against, so a fresh thread starts when the
  // user moves to a DIFFERENT selection rather than on every selection turn.
  // Resetting per turn left each follow-up with no history, so "redo again"
  // after /improve had no previous skill to continue.
  const lastSelectionKeyRef = useRef<string | null>(null);
  const lastWordPillTextRef = useRef<string>('');
  // AIVA-6902/6903/6904: `uploaded_this_turn` is stored ON these entries, restamped
  // by prepareAttachmentsToSend every turn, rather than being added to a throwaway
  // copy at send time. The backend's file selector keys its default on that flag, and
  // the approval resume needs it too — it sends this same list, and "this turn" has
  // moved on by the time the user approves. Keeping the flag here means the resume
  // reads the real value instead of re-deriving or fabricating one.
  const sessionUploadedFilesRef = useRef<ChatAttachment[]>([]);
  // Stores the draft_id returned by create_compose_draft so the compose.open
  // pill enrichment can inject it even though the tool schema doesn't expose it
  // as a passable parameter (additionalProperties: false on the ToolRegistry entry).
  const composeDraftIdRef = useRef<string>('');
  // The conversation the current turn is ABOUT — that of the read item open when
  // it started, REST id, null when none was. Every reply pill the turn emits is
  // bound to it in the pill sink, so a button clicked after the user moves to
  // another thread refuses rather than replying there (AIVA-8588, spec §4.3).
  // Read at turn start, not at emission, for the same reason `outlookSurface`
  // is: the answer was written for what the user asked from, even if they switch
  // while it streams. An approval resume continues the same turn and keeps it.
  const replyTargetConversationIdRef = useRef<string | null>(null);
  // Tracks attachment-draft (Open/Reply) pills the user has already clicked.
  // Re-clicking an old pill after the draft was sent is a silent no-op
  // (displayMessageForm has no failure callback, and the sent draft no longer
  // exists by its draft id), so on a second+ click we surface a blue info
  // toast pointing the user to their Sent folder. We still attempt the open in
  // case the draft was opened but not yet sent. AIVA-5595.
  const openedDraftIdsRef = useRef<Set<string>>(new Set());
  // Mirror of latestDraft.mode used by the pill filter at emission time.
  // setLatestDraft is async; without a ref, the filter would read stale
  // mode from the closure that started the turn.
  const latestDraftModeRef = useRef<EmailDraftMode | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>(() => restoredRef.current?.messages ?? []);
  const [input, setInput] = useState('');
  // Multi-select state for name-candidate disambiguation pills (id -> the
  // name text spliced into the input, plus the resolved email if the
  // backend supplied one), in click order. handleSend reads the mail side
  // of this to attach hidden metadata to the turn — see
  // buildResolvedContactsSuffix.
  const [selectedNameCandidates, setSelectedNameCandidates] = useState<
    Map<string, { name: string; mail?: string }>
  >(new Map());
  // Where in `input` the joined name list currently sits, and what text we
  // expect to find there — together they let a later toggle replace exactly
  // that span instead of guessing from string content (see spliceNameSelection).
  const nameListAnchorRef = useRef<NameListAnchor | null>(null);
  const lastJoinedNameTextRef = useRef<string>('');
  const [streaming, setStreaming] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  // Sanitised backend progress line (v2 `activity` events). Takes precedence
  // over the TOOL_LABELS mapping in ChatMessages while a turn streams.
  const [activityLine, setActivityLine] = useState<string | null>(null);
  // AIVA-8194: see TurnEventDeps.setDocReviewScope.
  const [docReviewScope, setDocReviewScope] = useState<string[] | null>(null);
  // latestDraft is the most recent draft produced in this chat session.
  // Sent back to the backend on every follow-up chat message so AIVA
  // remembers what was just drafted — lets the user say "make it more
  // formal" / "make it casual" after a draft. Replaced when AIVA
  // produces a newer draft.
  const [latestDraft, setLatestDraft] = useState<EmailDraft | null>(null);
  // AIVA-7482 — decisions the user has already made, re-sent on every turn and
  // every resume until the action they belong to resolves.
  //
  // Refs, not state: nothing renders from them, and they must be readable by a turn
  // that starts in the same tick as the click that set them. A state update would
  // not have landed yet, so the turn would send the previous value — which for a
  // slot click is no value at all, i.e. exactly the bug this fixes.
  const pendingConfirmationsRef = useRef<TurnConfirmations | null>(null);
  const confirmationsSetAtRef = useRef<number>(0);
  const capabilitySpanRef = useRef<CapabilitySpan | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  // Number of distinct files retained for the whole session (accumulated in
  // sessionUploadedFilesRef across turns). Exposed as reactive state — not read
  // off the ref — so the input bar can cap how many more files may be attached.
  const [sessionUploadedFileCount, setSessionUploadedFileCount] = useState(0);
  // Raw bytes of the retained set (base64 length × 3/4). The input bar's
  // cumulative budget check needs sizes, not a count — the whole set rides
  // every turn and the gateway 413s oversized requests (AIVA-7072).
  const [sessionUploadedFileBytes, setSessionUploadedFileBytes] = useState(0);
  // latestSearchResults mirrors the most recent email cards shown in chat.
  // Sent back to the backend on every follow-up turn so the LLM can resolve
  // "email 1" / "email 3" references without re-fetching.
  const [latestSearchResults, setLatestSearchResults] = useState<EmailSearchResult[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null!);
  // Every live stream's controller (main's AIVA-6436 shape). A single-slot ref
  // let an approval continuation clobber a concurrently live chat turn's
  // controller, so unmount/clear-chat cancelled only one of them.
  const activeAbortsRef = useRef<Set<AbortController>>(new Set());
  const userStoppedIdsRef = useRef<Set<string>>(new Set());
  // Live mirror of `messages` so callbacks that fire mid-stream (thread-id
  // adoption below) can persist the current transcript without re-rendering.
  const messagesRef = useRef<ChatMessage[]>(restoredRef.current?.messages ?? []);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // AIVA-6685 (V-08): chat transcripts now live in sessionStorage. Remove any a
  // pre-upgrade session left behind in localStorage once on mount, so the fix
  // clears existing exposure rather than only redirecting future writes.
  useEffect(() => {
    purgeLegacyLocalChatHistory();
  }, []);

  // AIVA-7506: flips true once word.docreview.anchor_findings dispatches —
  // see docReviewReadyRef's doc comment on UseChatReturn. A ref rather than
  // per-message state: the merge/replace semantics of the token-streaming
  // path that follows it in the same turn are not a safe thing for this
  // flag to depend on, and a ref sidesteps the question entirely.
  const docReviewReadyRef = useRef(false);

  // Chat v2 (AIVA-5929): the server thread store is the source of truth for
  // the transcript. Reconcile the cached render against it on mount, and keep
  // the live thread id in threadIdRef (null ⇒ the next send creates a fresh
  // server thread).
  const { threadIdRef, hydrated, resetThread, notePendingSend } = useThreadHydration({
    apiToken,
    host,
    // AIVA-7622: read live so a rehydrated Outlook action row matches whatever
    // surface is ACTUALLY open (e.g. a fresh Compose window), never the
    // surface the original turn happened to run in.
    outlookSurface: mailboxItem.surface,
    contextId,
    initialThreadId: restoredRef.current?.threadId ?? null,
    refresh: refreshTokens ?? undefined,
    setMessages,
    msgCounterRef,
    // AIVA-6899: hydration grafts the rendered transcript's client-only state
    // (action pills, email cards, badges) onto the server snapshot instead of
    // discarding it, so a task-pane remount doesn't strip the buttons.
    renderedRef: messagesRef,
  });

  // A skill message's chat session (see skillMessageSession.ts).
  // `previousConversationRef` holds the conversation it replaced, so "Previous
  // chat" restores it with its client-only pills and cards; after a reload only
  // its thread id survives (in the session info) and it is re-fetched.
  const [skillSession, setSkillSession] = useState<SkillSessionInfo | null>(() => {
    const stored = loadSkillSession(host, contextId);
    return stored && stored.threadId === restoredRef.current?.threadId ? stored : null;
  });
  const skillSessionRef = useRef(skillSession);
  skillSessionRef.current = skillSession;
  const previousConversationRef = useRef<{
    threadId: string | null;
    messages: ChatMessage[];
    msgCounter: number;
  } | null>(null);
  // Opening a message shows it at once and creates its thread in the background.
  // Sends wait on `skillThreadPendingRef` (false: the open failed or was left, so
  // the send is dropped); the epoch discards an open the user has moved on from.
  const skillThreadPendingRef = useRef<Promise<boolean> | null>(null);
  const skillOpenEpochRef = useRef(0);

  // AIVA-6899: re-seed the ordinal-reference context from the rehydrated
  // transcript. `latestSearchResults` is what lets a follow-up ("reply to email
  // 2") resolve without re-fetching, and it only ever lived in React state — so
  // after a remount the email cards were back on screen but the references
  // behind them silently weren't. Runs once, when hydration settles; the
  // messagesRef mirror above is declared first, so it is already current.
  useEffect(() => {
    if (!hydrated) return;
    const newestWithCards = [...messagesRef.current]
      .reverse()
      .find((m) => m.searchResults && m.searchResults.length > 0);
    if (newestWithCards?.searchResults) setLatestSearchResults(newestWithCards.searchResults);
  }, [hydrated, messagesRef]);

  // AIVA-7506: on a fresh window (sessionStorage cleared by AIVA-6685 — the
  // document was closed and reopened), hydration recovers threadIdRef and
  // replays the old assistant text, but docReviewReadyRef has no restore-
  // from-storage path (see its own doc comment above) — so a recovered
  // check-references reply showed up without its NBA card even though the
  // findings were still sitting in Cosmos. Ask the backend whether the
  // hydrated thread actually has any and, if so, open the card the same way
  // a live word.docreview.anchor_findings event would. Word-only: findings
  // only ever exist for a Word thread, and this is a speculative probe.
  const [, forceDocReviewReadyRerender] = useReducer((tick: number) => tick + 1, 0);
  useEffect(() => {
    if (!hydrated || host !== "word") return;
    const threadId = threadIdRef.current;
    if (!threadId) return;
    // A real AbortController — this effect can re-fire mid-session (e.g. on
    // an apiToken refresh) while an earlier probe for a different thread is
    // still in flight; without aborting it, the stale request could still
    // resolve after a newer one and write its (wrong-thread) answer into the
    // ref (AIVA-7506 review).
    const controller = new AbortController();
    void listFindings(threadId, apiToken, refreshTokens ?? undefined, controller.signal)
      .then((findings) => {
        // Explicitly reset to false on a zero-findings result too — not just
        // skip — so a stale `true` left over from a previous thread/probe
        // can't leave the doc-review card showing for a thread that has none.
        const ready = findings.length > 0;
        if (docReviewReadyRef.current === ready) return;
        docReviewReadyRef.current = ready;
        // The ref flip above is invisible to React on its own (see the ref's
        // own comment on why it's deliberately not state) — this state
        // update's only job is forcing the render that picks it up.
        forceDocReviewReadyRerender();
      })
      .catch(() => {
        // Best-effort only — a failed or aborted probe just leaves the ref as
        // it was, matching today's behaviour; DocReviewPanel would hit the
        // same endpoint and surface the same error if re-opened by hand.
      });
    return () => {
      controller.abort();
    };
  }, [hydrated, host, threadIdRef, apiToken, refreshTokens]);

  /** Register a stream's controller; returns the disposer to call when it settles. */
  const trackAbort = useCallback((controller: AbortController) => {
    activeAbortsRef.current.add(controller);
    return () => activeAbortsRef.current.delete(controller);
  }, []);

  /** Cancel every live stream — panel unmount and New Chat. */
  const abortAllStreams = useCallback(() => {
    for (const controller of activeAbortsRef.current) controller.abort();
    activeAbortsRef.current.clear();
  }, []);

  // If the panel unmounts mid-turn, abort the in-flight streams so we don't
  // leak the fetch and so the user-facing state machine doesn't try to
  // setState on an unmounted component.  The backend's Cosmos checkpoint
  // TTL is the second line of defence against orphaned graphs.
  useEffect(() => () => abortAllStreams(), [abortAllStreams]);

  // AIVA-5171: persist the conversation so it can be restored after the panel
  // is closed and reopened. Skip saves mid-stream so we write once per settled
  // turn rather than on every streamed token. An empty history is intentionally
  // saved too (it mirrors a cleared chat), but only after the restore on mount.
  useEffect(() => {
    if (streaming) return;
    // AIVA-5796: save under the document it belongs to (Word/PowerPoint), not
    // just the host, so each document keeps its own persisted thread. contextId
    // is null for Outlook, which keeps its legacy host-only bucket (AIVA-5171).
    saveChatHistory(
      host,
      {
        messages,
        threadId: threadIdRef.current,
        msgCounter: msgCounterRef.current,
      },
      contextId
    );
  }, [messages, streaming, host, contextId, threadIdRef]);

  // Adopt the server thread id carried by terminal SSE events (done/paused).
  // thread_reset=true means the server discarded an unknown/expired id and
  // started fresh; a null local id means this send created the thread. Either
  // way the rendered transcript is kept — only the id changes.
  const handleThreadIdResolved = useCallback(
    (threadId: string, reset: boolean) => {
      if (!reset && threadIdRef.current !== null) return;
      threadIdRef.current = threadId;
      setChatCleared(host, contextId, false);
      saveChatHistory(
        host,
        {
          messages: messagesRef.current,
          threadId,
          msgCounter: msgCounterRef.current,
        },
        contextId
      );
    },
    [host, contextId, threadIdRef]
  );

  // DeckorateChat mints its own session ids, which /api/v2/chat does not know
  // (and the reverse), so the two routes cannot share threadIdRef: a v2 thread
  // id sent to /deckorate/chat/resume, or a Deckorate session hydrated as a v2
  // thread, would be rejected or continue the wrong conversation. Kept in
  // memory only — the persisted, hydrated thread is the general one.
  const deckorateThreadIdRef = useRef<string | null>(null);
  const handleDeckorateThreadIdResolved = useCallback((threadId: string, reset: boolean) => {
    if (!reset && deckorateThreadIdRef.current !== null) return;
    deckorateThreadIdRef.current = threadId;
  }, []);

  // Bridge: every `ui.pill.*` handler emits one pill via the module-level
  // sink in integrations/tools/outlook/pill-bridge.ts. Each LLM tool call
  // produces a separate emit, so the sink APPENDS to the latest assistant
  // message's pill row rather than replacing it — otherwise a turn that
  // calls 3 pill tools in sequence would render only the last one.
  // Per-turn isolation is automatic: a new assistant message is created at
  // the start of every turn, so prior turns' pills don't bleed in.
  useEffect(() => {
    return registerPillSink((pills) => {
      // Stamp the authoritative draft_id (captured from the create_reply_draft /
      // create_compose_draft tool result) into attachment-draft pills. The LLM
      // hand-copies that long opaque Graph id into the pill args and routinely
      // drops/duplicates a character (AIVA-5594), which makes displayMessageForm
      // silently no-op. The tool result is the source of truth, so freeze it
      // into the pill here rather than trusting the model's copy.
      const withDraftIds = pills.map((p) =>
        p.type === 'office_action' && p.handler === 'attachment.open_draft' && composeDraftIdRef.current
          ? { ...p, args: { ...p.args, draft_id: composeDraftIdRef.current } }
          : p
      );
      // Every pill reaches the row through here — the frontend-built Outlook bar
      // and the model's pill tools alike — so binding at this one point covers
      // every reply button whatever produced it.
      const stamped = bindReplyPillsToConversation(withDraftIds, replyTargetConversationIdRef.current);
      setMessages((prev) => mergePillsIntoLastAssistant(prev, stamped));
    });
  }, []);

  // Bridge: the `ui.render_email_cards` handler emits cards via email-cards-bridge.ts.
  useEffect(() => {
    return registerEmailCardSink((cards, querySummary) => {
      setLatestSearchResults(cards);
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last) {
          updated[updated.length - 1] = { ...last, searchResults: cards, searchQuerySummary: querySummary };
        }
        return updated;
      });
    });
  }, []);

  const nextMsgId = () => `msg-${++msgCounterRef.current}`;

  // Builds the per-turn dependencies for createTurnEventHandler. Shared by
  // runChatTurn and the approval-continuation flow so both streams update the
  // chat state identically. Callers guarantee a non-null host (runChatTurn and
  // the approval hook both return early when no Office host was detected).
  const makeTurnDeps = useCallback(
    (
      assistantMsgId: string,
      turnTimestamp: number,
      turnOpts: { forcedSkillKey?: string; autoApply?: boolean; promptText?: string; route?: ChatRoute } = {}
    ): TurnDeps => ({
      host: host as Channel,
      wordDoc,
      assistantMsgId,
      turnTimestamp,
      forcedSkillKey: turnOpts.forcedSkillKey,
      autoApply: turnOpts.autoApply,
      // AIVA-7026: the user's prompt, so the Outlook action bar reshapes for the
      // intent they typed (reply / new-email / compose / meeting) rather than the
      // skill that ran. Absent on approval-resume turns (no fresh prompt).
      promptText: turnOpts.promptText,
      setMessages,
      setActiveTool,
      setLatestDraft,
      recordConfirmedExternals,
      setLatestSearchResults,
      pillsEmittedRef,
      listEmailsHadResultsRef,
      lastWordPillTextRef,
      composeDraftIdRef,
      latestDraftModeRef,
      setActivityLine,
      setDocReviewScope,
      // Each route adopts the id its own backend minted (see deckorateThreadIdRef).
      onThreadIdResolved:
        turnOpts.route === 'deckorate' ? handleDeckorateThreadIdResolved : handleThreadIdResolved,
      markDocReviewReady: () => {
        docReviewReadyRef.current = true;
      },
      // Half the key into the Outlook pill table. Read at turn start rather than
      // at `done` so the buttons describe the surface the user actually asked
      // from, even if they switch windows while the answer streams.
      outlookSurface: mailboxItem.surface,
      // PowerPoint builds its action bar for general-route turns only.
      route: turnOpts.route,
      // AIVA-7506: word.docreview.scan's handler POSTs its deterministic
      // findings straight to the bulk-create endpoint, so it needs the same
      // auth context every other backend call here already has.
      threadIdRef,
      apiToken,
      refresh: refreshTokens ?? undefined,
      capabilitySpan: capabilitySpanRef.current ?? undefined,
    }),
    [
      host,
      wordDoc,
      handleThreadIdResolved,
      handleDeckorateThreadIdResolved,
      mailboxItem.surface,
      threadIdRef,
      apiToken,
      refreshTokens,
    ]
  );

  // Client-owned resume context (AIVA-5925): the approval / tool-result resume
  // re-sends the same context the initial turn carried, so the backend can
  // rebuild the paused tool with its message-id / draft / search context. Kept
  // client-side (no server persistence) per the architecture decision — see
  // docs/adr/ADR-chat-v2-hitl-resume-context.md. Computed on demand so it
  // reflects the open email + latest draft at the moment the user decides.
  const getResumeContext = useCallback(
    () => ({
      emailContext: buildEmailContext(mailboxItem),
      documentContext: buildDocumentContext(host, wordDoc),
      // The surface can change between the pause and the decision (the user may
      // open a compose window while an approval card is waiting), so this is
      // read at decision time along with the rest of the resume context.
      surface: mailboxItem.surface,
      latestDraft,
      latestSearchResults,
      // Re-send session files so a HITL-gated attachment draft is still bound
      // on approval — the attachment write tools bind only when attachments are
      // present (AIVA-6391).
      //
      // AIVA-6902/6903/6904: this used to stamp uploaded_this_turn: true on EVERY
      // session file, on the reasoning that the flag was moot because the approved
      // tool call carries its own file_numbers. That holds only when file_numbers
      // is present — and it is absent for the common "attach this" case, where the
      // backend falls back to the this-turn files. Marking everything this-turn
      // therefore re-presented the whole session history as if it had just been
      // uploaded, which defeats the backend's fail-closed selector on precisely
      // the path the router-level guard doesn't cover. Replay the payload the
      // paused turn actually sent, so the resume sees the same files with the same
      // flags. Falls back to the stored flags (never a fabricated true) when the
      // turn wasn't sent from this mount.
      attachments: resumeAttachments(sessionUploadedFilesRef.current),
      // AIVA-7482 — the approval resume is the request that most needs these: it
      // rebuilds every per-request surface empty, so without them an approved
      // booking is re-refused for the slot the user already picked.
      confirmations: readConfirmations(),
      userDisplayName: signedInDisplayName(),
    }),
    [mailboxItem, wordDoc, host, latestDraft, latestSearchResults]
  );

  const handleApprovalDecision = useApprovalDecision({
    apiToken,
    host,
    threadIdRef,
    refresh: refreshTokens ?? undefined,
    setMessages,
    setStreaming,
    nextMsgId,
    makeTurnDeps,
    trackAbort,
    userStoppedIdsRef,
    getResumeContext,
    onCapabilityRunEnded: () => {
      capabilitySpanRef.current = null;
    },
  });

  // Core chat-turn driver, callable from both the typed-input handler and
  // chat_intent pill clicks. `hidden=true` skips the visible user-message
  // bubble (used for one-tap pill shortcuts where echoing the pill text
  // back as a user bubble would clutter history).
  //
  // Two of its setMessages updaters (freezeNameCandidateSelection,
  // staleUndecidedApprovals) are module-level named helpers rather than
  // inline closures — extracted to keep this function's cognitive
  // complexity under the Sonar limit (S3776), same as missingRequiredUpload
  // / prepareAttachmentsToSend / drainPendingToolCalls further down.
  const runChatTurn = useCallback(
    async (
      text: string,
      opts: {
        hidden?: boolean;
        autoApply?: boolean;
        forcedSkillKey?: string;
        displayText?: string;
        attachedFileNames?: string[];
        files?: File[];
        capabilityTrigger?: CapabilityTrigger;
        /** AIVA-7571 - the selection this turn is REPLAYING (regenerate only).
         *  Satisfies the selection guard with the state the original turn ran
         *  under, instead of the live state the Regenerate click just cleared. */
        replaySelection?: { readonly text: string; readonly hasSelection: boolean };
        /** AIVA-6390 - a multipart step resend for an upload-contextual skill:
         *  the planner only chose this skill because a file was genuinely
         *  attached to the message that produced the plan, so missingRequiredUpload's
         *  "nothing currently staged" check does not apply here. */
        attachmentKnownPresent?: boolean;
        /** AIVA-8157 - this turn REPLAYS a planned step, so the backend must
         *  not re-plan it. A user-typed slash sets forcedSkillKey too but is
         *  a fresh request that may well name two tasks, and the planner has
         *  to see it. */
        fromPlanStep?: boolean;
        /** PowerPoint only: `deckorate` sends this turn to DeckorateChat when
         *  the Deckorate module is enabled. Set by Deckorate entry points, never
         *  by the composer. See resolveChatRoute. */
        route?: ChatRoute;
        /** AIVA-8792 — this is the Deckorate re-send of a turn v2 handed off.
         *  Carries the first pass's message ids so its user bubble is not sent
         *  to Deckorate twice (once as history, once as the message). Also
         *  stops a second handoff: the re-send never goes back to v2. */
        handoffOf?: { readonly userMsgId: string; readonly assistantMsgId: string };
        /** DI-01 — the summary pass: the deck to send and the card metadata. */
        deckSummary?: DeckSummaryTurn;
        /** Local bubbles this turn's bubbles replace, in the same update. */
        replacesMessageIds?: readonly string[];
      } = {}
    ) => {
      if (!text) return;
      // The handoff re-send starts the moment the v2 pass's `finally` has run,
      // before React re-renders — so `streaming` here can still be the stale
      // `true` of that pass. `sendingRef` is already reset; trust that instead.
      if (opts.handoffOf ? sendingRef.current : streaming || sendingRef.current) return;
      pillsEmittedRef.current = false;
      listEmailsHadResultsRef.current = false;
      lastWordPillTextRef.current = '';
      composeDraftIdRef.current = '';
      replyTargetConversationIdRef.current = mailboxItem.surface === 'read' ? mailboxItem.conversationId : null;
      // AIVA-7506: a fresh turn closes the previous turn's inline doc-review
      // card — it answered a specific /check-references run, and leaving it
      // up while the user asks something unrelated reads as stale leftover
      // UI. Re-running /check-references sets it true again like any other
      // turn.
      docReviewReadyRef.current = false;
      // A real send starts — freeze the current name-candidate selection onto
      // the message that produced it (so its pills stay checked forever,
      // matching what was actually sent), THEN forget the live selection so
      // clicking a stale pill later starts a fresh list rather than resuming
      // into whatever this turn's input used to be.
      if (selectedNameCandidates.size > 0) {
        const ids = [...selectedNameCandidates.keys()];
        setMessages((prev) => freezeNameCandidateSelection(prev, ids));
      }
      setSelectedNameCandidates(new Map());
      nameListAnchorRef.current = null;
      lastJoinedNameTextRef.current = '';
      // Starting a new turn abandons any not-yet-decided approval server-side
      // (agent_base._sanitise_orphan_tool_messages stubs out the old gated
      // tool call). Grey it out immediately so it doesn't sit there looking
      // clickable until the user taps it and gets turned away.
      setMessages(staleUndecidedApprovals);
      // AIVA-8971: /shownotes and /slide run as the request they stand for, so
      // the local handlers below answer the command exactly as they answer
      // those words typed out. /summarise is read by deckSummaryIntent itself.
      const turnText = localCommandRequest(host, text) ?? text;
      // DI-02 — PowerPoint "list the speaker notes" runs locally and must keep
      // working with the AI offline, so it is resolved before the AI guard.
      if (host === 'powerpoint' && matchListNotesIntent(turnText)) {
        sendingRef.current = true;
        try {
          await runLocalSpeakerNotesTurn({ text: turnText, host, hidden: opts.hidden, setMessages, nextMsgId });
        } finally {
          sendingRef.current = false;
        }
        return;
      }
      // DI-04 ("what's on this slide") is answered from Office.js alone, so it
      // runs BEFORE the AI-offline guard below: the spec requires it to keep
      // working with the AI provider disconnected (ST-03, DI-04 AC-5).
      if (isLocalDeckIntelligenceTurn(host, turnText, opts.forcedSkillKey)) {
        sendingRef.current = true;
        try {
          const answer = await answerSlideContentRequest({
            trigger: opts.capabilityTrigger ?? 'natural_language',
            question: matchShapeQuestion(turnText) ?? undefined,
          });
          const now = Date.now();
          setMessages((prev) => [...prev, ...localExchangeMessages(turnText, answer, opts.hidden, now, nextMsgId)]);
        } finally {
          sendingRef.current = false;
        }
        return;
      }
      // Can't run without an Office host, or answer while the AI provider is
      // disconnected (AIVA-6350) — post a friendly bubble and stop before any
      // backend call. Both guards resolve to one message via sendBlockedMessage.
      const blockedMessage = sendBlockedMessage(host, aiProviderConnected);
      if (blockedMessage) {
        setMessages((prev) => [
          ...prev,
          { id: nextMsgId(), role: 'assistant', content: blockedMessage, timestamp: Date.now() },
        ]);
        return;
      }
      // sendBlockedMessage returns a message whenever host is null, so past the
      // guard host is guaranteed set — capture it as a non-null Channel so the
      // downstream calls that require one type-check without a null branch.
      const activeHost: Channel = host as Channel;
      // DI-01 — needs the AI, so it runs after the AI guard; matched synchronously so no second send slips in.
      const deckIntent = deckSummaryIntent(turnText, activeHost, opts.deckSummary);
      if (deckIntent) {
        await runDeckSummary({
          text: turnText,
          bulletCount: deckIntent.bulletCount,
          hidden: opts.hidden,
          setMessages,
          nextMsgId,
          sendingRef,
          resend: runChatTurnRef.current,
        });
        return;
      }
      // SC-06 — needs the AI, so it runs after the AI-offline guard (AC-4: offline, that guard answers instead).
      // A follow-up to a draft or summary ("make it punchier") refines that reply, not the slide.
      const afterDraft = lastReplyIsDraft(messagesRef.current);
      const rephrase = rephraseSlideRequest(activeHost, text, opts.forcedSkillKey, messagesRef.current);
      if (rephrase) {
        sendingRef.current = true;
        try {
          await runRephraseSlideTurn({
            text,
            instruction: rephrase.instruction,
            hidden: opts.hidden,
            setMessages,
            nextMsgId,
            apiToken: await resolveTurnBearer(ensureFreshToken, apiToken),
            refresh: refreshTokens ?? undefined,
            trigger: opts.capabilityTrigger,
            spans: slideRewriteSpansRef.current,
          });
        } finally {
          sendingRef.current = false;
        }
        return;
      }
      sendingRef.current = true;

      // Re-read the Word context for THIS turn rather than trusting the
      // event-driven cache: Word does not reliably fire
      // DocumentSelectionChanged on a deselect, so `wordDoc` can still hold a
      // selection the user already let go of — which made a re-run slash
      // command answer over the previous selection instead of the current
      // document. Every consumer below (guards, thread reset, frozen
      // selection, document context) reads this resolved value.
      const liveWordDoc = await resolveLiveWordDoc(activeHost, wordDoc, resolveSendWordDoc);

      // TON-1: same reasoning as liveWordDoc above, for Outlook compose
      // bodies — see resolveLiveMailboxItem.
      const liveMailboxItem = await resolveLiveMailboxItem(activeHost, mailboxItem);

      // Slash-command resolution: if the input starts with a recognised
      // slash, extract the skill key for ``forced_skill_key`` and use the
      // remaining text (or the command's autoSendMessage fallback) as the
      // message body. The slash sigil itself never reaches the LLM — skill
      // prompts are tuned for natural-language entry. When opts.forcedSkillKey
      // is already set (palette / pill-driven turn), prefer it and skip
      // re-parsing.
      //
      // Ensure the registry is hydrated before resolving the slash. The
      // ChatTabPanel useEffect fires loadCommandsForHost on mount, but it
      // races against a fast typer; if the cache is still empty when the
      // user hits Send, lookupCommand would miss and the raw "/cmd args"
      // would leak into the user bubble (AIVA-5057). Idempotent and cheap
      // on a cache hit — only the first slash send pays the network cost.
      // Also runs when a forcedSkillKey is passed (e.g. from a pill click) so
      // downstream lookups like findCommandBySkillKey see a hydrated cache.
      // Predicate extracted to keep runChatTurn under Sonar's complexity cap.
      const resolved = await resolveTurnSkill({
        text,
        forcedSkillKey: opts.forcedSkillKey,
        host: activeHost,
        apiToken,
        refreshTokens,
        hasSubject: Boolean(mailboxItem.subject),
        hasSelection: Boolean(liveWordDoc?.hasSelection),
      });
      const messageBody = resolved.messageBody;
      const forcedSkillKey: string | undefined = resolved.forcedSkillKey ?? undefined;

      // Upload-contextual skills (/attach-reply, /attach-compose, /summarise-file)
      // require a file supplied on THIS request — the spec scopes attachments to
      // the chat turn they arrived on (AiVA-Outlook-Feature-Specification, DR-01:
      // "If the user attached a file on the chat turn..."), not to the session.
      // AIVA-6902/6903/6904: checking sessionUploadedFilesRef here let any file
      // ever uploaded earlier in the session satisfy the check, so re-invoking one
      // of these commands with nothing currently attached silently reused a stale
      // file instead of prompting the user — session-wide reuse is deliberately
      // still supported, but only when the user names the earlier file in a plain
      // chat message (file_numbers), not for these dedicated commands.
      const missingRequiredUpload = (): boolean =>
        forcedSkillKey !== undefined &&
        UPLOAD_CONTEXTUAL_SKILL_KEYS.has(forcedSkillKey) &&
        attachedFiles.length === 0 &&
        !(opts.files && opts.files.length > 0) &&
        !opts.attachmentKnownPresent;

      // AIVA-8957: a turn refused before it runs — no selection, no subject,
      // no upload, no Microsoft 365 — is answered IN THE CHAT in Word: the
      // user's request and the reason it can't run stay in the conversation.
      // A toast vanished after a few seconds and left no trace of the attempt,
      // so a later "try again" had nothing to go on. Other hosts keep the
      // toast. Either way nothing is sent to the AI: the turn never ran.
      const refuseTurn = (message: string): void => {
        if (host === 'word') {
          const bubble = selectUserBubbleContent(
            text,
            messageBody,
            resolved.forcedSkillKey,
            opts.forcedSkillKey,
            opts.displayText
          );
          setMessages((prev) => [...prev, ...localExchangeMessages(bubble, message, opts.hidden, Date.now(), nextMsgId)]);
        } else {
          emitToast({ title: message, intent: 'info' });
        }
        sendingRef.current = false;
      };

      if (missingRequiredUpload()) {
        refuseTurn('Upload a file first, then try that again.');
        return;
      }

      // /?, /help, /skills, /list, and SAF-1's unmatched-slash refusal are
      // all answered locally, without an LLM turn — see
      // resolveLocalTranscriptAnswer.
      const localAnswer = await resolveLocalTranscriptAnswer({
        text,
        host,
        activeHost,
        apiToken,
        refreshTokens,
        forcedSkillKey,
        // G-12 (AIVA-6804): the PowerPoint listing adds a Slide Design group
        // only when the Deckorate module is enabled for this user.
        builtInOptions: { deckorateEnabled: host === 'powerpoint' && (isDeckorateEnabled?.() ?? false) },
      });
      if (localAnswer) {
        const now = Date.now();
        setMessages((prev) => [...prev, ...localExchangeMessages(text, localAnswer, opts.hidden, now, nextMsgId)]);
        sendingRef.current = false;
        return;
      }

      // Selection-required skills (declared on the skill document via
      // inputRequirements.requiresWordSelection) short-circuit here rather than
      // silently sending the entire document at the LLM — see refuseTurn for
      // how the reason is shown.
      const selectionNudge = resolveSelectionGuardNudge({
        forcedSkillKey,
        host,
        hasSelection: guardSelectionState(opts.replaySelection, liveWordDoc),
        rawText: text,
      });
      if (selectionNudge) {
        refuseTurn(selectionNudge);
        return;
      }

      // Drafting skills that were given neither a subject nor a selection
      // short-circuit here (inputRequirements.requiresSubjectOrSelection).
      const subjectNudge = resolveSubjectGuardNudge({
        forcedSkillKey,
        host,
        hasSelection: guardSelectionState(opts.replaySelection, liveWordDoc),
        rawText: text,
      });
      if (subjectNudge) {
        refuseTurn(subjectNudge);
        return;
      }

      // PowerPoint commands on the selected shape's text (/rewrite) need one
      // selected (inputRequirements.requiresSlideTextSelection).
      const slideNudge = await resolveSlideSelectionGuardNudge({
        forcedSkillKey,
        host,
        rawText: text,
        isTextSelected: isTextSelectionPresent,
      });
      if (slideNudge) {
        refuseTurn(slideNudge);
        return;
      }

      // Microsoft 365-dependent skills short-circuit here so no AI call is made
      // in place of an operation that cannot succeed.
      const m365Nudge = resolveM365GuardNudge({ forcedSkillKey, host, m365Connected, rawText: text });
      if (m365Nudge) {
        refuseTurn(m365Nudge);
        return;
      }

      // A send is starting — invalidate any in-flight thread hydration so a
      // stale server snapshot can't clobber the new turn (race guard).
      notePendingSend();

      // Start a fresh server thread (null the thread id — creation stays lazy
      // on the next send) when the thing being worked on CHANGES: a different
      // Word selection, or a different Outlook email. The full rules live on
      // resolveThreadReset (extracted for S3776).
      //
      // AIVA-8357: the selection is fingerprinted by its own text (see
      // selectionFingerprint) so a follow-up on the SAME selection keeps the
      // thread, and the router can still see the previous turn's skill.
      const { currentEmailId, currentSelectionKey, shouldStartFreshThread } = resolveThreadReset({
        forcedSkillKey,
        host,
        hasSelection: Boolean(liveWordDoc?.hasSelection),
        itemId: mailboxItem.itemId,
        lastEmailId: lastEmailIdRef.current,
        selectionKey: selectionFingerprint(liveWordDoc),
        lastSelectionKey: lastSelectionKeyRef.current,
      });
      lastEmailIdRef.current = currentEmailId;
      lastSelectionKeyRef.current = currentSelectionKey;
      if (shouldStartFreshThread) {
        resetThread();
        deckorateThreadIdRef.current = null;
        // AIVA-7482 — a fresh thread is a different conversation, so a slot picked in
        // the old one must not authorise a booking here. New Chat clears these too;
        // this covers the other route to a new thread, which a user hits without
        // meaning to.
        clearConfirmations();
      }
      // v2 sends no history in the body — the server thread is the source of
      // truth. The parameter is kept so the streamChat call site stays stable.
      const historyToSend = shouldStartFreshThread ? [] : historyForTurn(messages, opts.handoffOf);

      const userMsgId = nextMsgId();
      const assistantMsgId = nextMsgId();
      const turnTimestamp = Date.now();

      // Computed for every turn — hidden ones included — because it also rides
      // the request as `display_content`, so the server transcript stores what
      // the pane rendered and a relaunch rehydrates the friendly bubble instead
      // of the raw engineered prompt (the AIVA-6899 pairing only survives while
      // the session cache does; a full close/reopen loses it).
      const bubbleContent = selectUserBubbleContent(
        text,
        messageBody,
        resolved.forcedSkillKey,
        opts.forcedSkillKey,
        opts.displayText
      );
      if (!opts.hidden) {
        setMessages((prev) => [
          ...prev,
          {
            id: userMsgId,
            role: 'user',
            content: bubbleContent,
            timestamp: turnTimestamp,
            attachedFileNames: opts.attachedFileNames,
            // AIVA-6899: same-session remounts still pair the clean bubble to
            // the stored form via this key; older threads stored `messageBody`
            // raw, so the key keeps rehydration working for them too.
            ...(bubbleContent === messageBody ? {} : { sentContent: sentContentKey(messageBody) }),
          },
        ]);
      }
      setStreaming(true);
      setActivityLine(null);
      beginHighlightTurn();

      // Per-turn stream state + event handler (extracted to turnEvents.ts,
      // AIVA-5929). A fresh TurnState is composed for every turn; the handler
      // closes over it and the hook-level deps.
      const turnState = createTurnState();
      if (opts.capabilityTrigger) {
        capabilitySpanRef.current = startCapability({
          trigger: opts.capabilityTrigger,
          surface: host === 'outlook' ? (mailboxItem.surface ?? 'none') : undefined,
        });
      }
      // Fixed for the whole turn: the resume calls below must go to the backend
      // that paused, even if the module is toggled while a tool call runs.
      const route = resolveChatRoute(host, isDeckorateEnabled?.() ?? false, opts.route, forcedSkillKey);
      const turnDeps = makeTurnDeps(assistantMsgId, turnTimestamp, {
        route,
        forcedSkillKey,
        autoApply: opts.autoApply,
        // AIVA-7026: the intent the Outlook action bar reshapes for is read from
        // this. `messageBody` is the sent, slash-stripped form — a Book-meeting
        // pill sends "Set up a meeting", a slot pick sends its scheduling params,
        // a typed "draft an agenda" arrives verbatim.
        promptText: messageBody,
      });
      const handleEvent = withDeckSummaryFailureToast(createTurnEventHandler(turnState, turnDeps), opts.deckSummary);
      // skillKey seeded from forcedSkillKey up front (not just on `done`) so a
      // turn stopped mid-stream still leaves regenerate() able to see which
      // skill produced it — see turnEvents.ts's updateAssistant carry-forward.
      setMessages((prev) => [
        // DI-01: same update as the user bubble, so the replaced "Reading deck…" pair never flickers.
        ...(opts.replacesMessageIds ? prev.filter((m) => !opts.replacesMessageIds?.includes(m.id)) : prev),
        {
          id: assistantMsgId,
          role: 'assistant',
          content: '',
          timestamp: turnTimestamp,
          skillKey: forcedSkillKey,
          ...frozenSelectionFragment(liveWordDoc),
          ...(opts.deckSummary ? { deckSummary: opts.deckSummary.meta } : {}),
        },
      ]);
      // Auto-scroll is handled by a useLayoutEffect in ChatMessages keyed on
      // the messages array — every append (user bubble, empty assistant bubble,
      // action pills attached, search results attached) triggers a smooth scroll
      // that lands at the new bottom. See AIVA-5054.

      const emailCtx = buildEmailContext(liveMailboxItem);
      const memoryBlock = await buildCompletedActionsMemory(host, forcedSkillKey);
      const replayDoc = applyReplaySelection(liveWordDoc, opts.replaySelection);
      // SC-01 (AIVA-6809): a general PowerPoint turn carries the current slide —
      // the selection, or else the whole slide — read now, so it matches what the
      // user is looking at as they send. Word keeps its own document context.
      const slideCtx = host === 'powerpoint' ? await slideContextForRoute(route) : null;
      // DI-01: a summary turn sends the whole deck instead of the current slide (re-sent on every resume).
      const documentCtx: DocumentTurnContext | null =
        host === 'powerpoint'
          ? powerPointDocumentContext(opts.deckSummary, slideCtx)
          : buildDocumentContext(host, replayDoc, { forcedSkillKey, memoryBlock });
      // SC-03 (AIVA-6754): the same read says whether a shape with text is
      // selected, which swaps the bar's first button to "Replace selection".
      if (host === 'powerpoint') turnDeps.pptTextShapeSelected = slideCtx?.hasSelection === true;
      const abort = new AbortController();
      // Registered in the live-stream set so unmount / New Chat cancels this turn
      // even while an approval continuation is streaming alongside it.
      const untrackAbort = trackAbort(abort);
      turnDeps.signal = abort.signal;

      // Accumulate this turn's uploads into the session set (dedup by content),
      // then tag which files were attached THIS turn. Extracted to keep
      // runChatTurn's cognitive complexity under the Sonar limit (S3776).
      const prepareAttachmentsToSend = async () => {
        const newAttachments = opts.files?.length
          ? await Promise.all(
              opts.files.map(async (f) => ({
                name: f.name,
                content_type: f.type || 'application/octet-stream',
                content_base64: await readFileAsBase64(f),
              }))
            )
          : undefined;
        if (newAttachments?.length) {
       
          const existing = sessionUploadedFilesRef.current;
          const seen = new Set(existing.map((f) => f.content_base64));
          const merged = existing.slice();
          for (const att of newAttachments) {
            if (!seen.has(att.content_base64)) {
              seen.add(att.content_base64);
              merged.push(att);
            }
          }
          sessionUploadedFilesRef.current = merged;
          setSessionUploadedFileCount(merged.length);
          setSessionUploadedFileBytes(
            merged.reduce((sum, f) => sum + Math.floor((f.content_base64.length * 3) / 4), 0)
          );
        }
        // Restamp the flag on the STORED entries, not on a copy: the approval resume
        // sends this same list later, and a flag that lived only on the request
        // payload left it with no way to tell a just-uploaded file from an old one.
        // A multipart plan step carries no files and must keep the originating turn's
        // flags — see stampUploadFlags.
        const thisTurnContents = new Set((newAttachments ?? []).map((a) => a.content_base64));
        sessionUploadedFilesRef.current = stampUploadFlags(
          sessionUploadedFilesRef.current,
          thisTurnContents,
          !newAttachments?.length && opts.attachmentKnownPresent === true
        );
        return resumeAttachments(sessionUploadedFilesRef.current);
      };
      const attachmentsToSend = await prepareAttachmentsToSend();

      // AIVA-5861 pre-flight: refresh the bearer BEFORE sending when the tracked
      // token expiry says it is stale, so we never send a token we already know is
      // expired (which either 401s or — worse — passes APIM but fails the backend
      // OBO exchange mid-turn with no 401 to retry). On dialog-sign-in hosts the
      // background timers can't refresh silently, so after idle this up-front
      // refresh is what actually renews the token on the user's send. Falls back to
      // the current apiToken; the reactive runWithAuthRetry below still covers a
      // token that lapses between here and the request.
      // Mutable token cell so a successful refreshTokens() on a 401 retry can
      // hand the new bearer to subsequent resumeChat calls in the same turn
      // without waiting for the AuthState context to re-render.
      let currentToken = await resolveTurnBearer(ensureFreshToken, apiToken);

      // A Deckorate-route turn (see resolveChatRoute) goes to the dedicated
      // DeckorateChat endpoints instead of the general /chat surface — a
      // different, PowerPoint-only skillset gated server-side by an explicit
      // is_deckorate flag. A general PowerPoint turn takes /api/v2/chat with
      // host=powerpoint like Word and Outlook. Everything below this branch
      // point (SSE event handling, frontend-tool dispatch, resume) is shared
      // unchanged: both backends emit the same SSE event vocabulary, and the
      // Outlook/Word-specific parsing in handleToolEnd is keyed on tool names
      // Deckorate tools won't match.
      const isDeckorate = route === 'deckorate';
      let turnFailed = false;
      let handoffToDeckorate = false;
      let handoffToRephrase = false;

      try {
        currentToken = await runWithAuthRetry(
          (token) =>
            isDeckorate
              ? // Null on the first send — the Deckorate route mints the session
                // id and returns it on the done/paused event, same as v2 below.
                streamDeckorateChat(messageBody, historyToSend, deckorateThreadIdRef.current, token, handleEvent, {
                  signal: abort.signal,
                })
              : streamChat(
                  messageBody,
                  historyToSend,
                  // Null until the server mints a thread on the first send — the
                  // v2 body simply omits thread_id and the done/paused terminal
                  // events hand back the id we adopt via onThreadIdResolved.
                  threadIdRef.current,
                  activeHost,
                  token,
                  handleEvent,
                  {
                    emailContext: emailCtx,
                    documentContext: documentCtx,
                    forcedSkillKey,
                    fromPlanStep: opts.fromPlanStep,
                    // The transcript stores what the pane rendered: the friendly
                    // bubble for engineered pill/slash prompts, nothing for a
                    // hidden turn — so a relaunch shows what the user saw, not
                    // time_source=/verified_slot_id= internals.
                    displayContent: bubbleContent,
                    hiddenTurn: opts.hidden,
                    latestDraft,
                    latestSearchResults,
                    attachments: attachmentsToSend,
                    // Read vs compose. Sent separately from emailContext, which is
                    // null on an empty subject — so a fresh compose window would
                    // otherwise look identical to "no email open".
                    surface: mailboxItem.surface,
                    // AIVA-7482 — decisions already made (clicked slot, approved
                    // recipients) and who the user is. The turn that needs them is
                    // never the turn they were made on.
                    confirmations: readConfirmations(),
                    userDisplayName: signedInDisplayName(),
                    disclaimerText,
                    personaDisplayName,
                    // Server threads isolate per context the same way the
                    // localStorage cache does (Word doc URL bucket, AIVA-5796).
                    contextId,
                    // Lets the backend refuse a Graph-dependent skill after routing
                    // instead of running an agent that would fail on a Graph call.
                    m365Connected,
                    // AIVA-8792 — lets v2 hand a Deckorate request back to us.
                    // PowerPoint only; other hosts send nothing ("unknown").
                    deckorateEnabled: host === 'powerpoint' ? (isDeckorateEnabled?.() ?? false) : undefined,
                    // AIVA-9184 — lets v2 hand "improve this slide" back to the
                    // slide rewrite (SC-06) when the phrase regex missed it.
                    // Not after a draft or summary, so the router can't pick the slide rewrite.
                    rephraseSlideHandoff: host === 'powerpoint' && !afterDraft,
                    signal: abort.signal,
                  }
                ),
          currentToken,
          refreshTokens
        );

        // Frontend-tool dispatch loop. The backend may emit multiple
        // frontend_tool_call + paused pairs into the SAME stream when the
        // LLM tool-calls in parallel (e.g. a skill ending its turn with
        // five pill_* tools). Drain every queued call locally — each pill
        // handler emits its pill into the UI — then POST one /chat/resume.
        // The backend's astream_resume resolves ALL unresolved tool_call_ids
        // in the checkpoint against the same result payload, so a single
        // resume per batch is sufficient and matches what the graph expects.
        // Bounded by the backend's recursion limit (resumes may queue more
        // tool calls), so no client-side counter is needed.
    
        const drainPendingToolCalls = async () => {
          while (turnState.pendingQueue.length > 0) {
            const batch = turnState.pendingQueue.splice(0);
            let lastResult: Record<string, unknown> = {};
            for (const next of batch) {
              lastResult = await dispatchPendingToolCall(turnState, turnDeps, next); // NOSONAR tool calls must run in order
            }
            const lastCall = batch[batch.length - 1]!;
            // Use currentToken (potentially refreshed by streamChat or a prior
            // resume in the same turn) and route through runWithAuthRetry so a
            // 401 on resume also triggers one silent refresh.
            currentToken = await runWithAuthRetry( // NOSONAR resume depends on the last tool result
              (token) =>
                isDeckorate
                  ? resumeDeckorateChat(
                      // As with resumeChat below: the paused event that preceded
                      // this resume carries the thread id, so it is adopted by
                      // now. The fallback only fires if the backend violated its
                      // terminal-event contract, and the server rejects it.
                      deckorateThreadIdRef.current ?? '',
                      lastCall.toolCallId,
                      lastResult,
                      token,
                      handleEvent,
                      {
                        signal: abort.signal,
                        history: historyToSend,
                      }
                    )
                  : resumeChat(
                      // The paused terminal event that preceded this resume carries
                      // the thread id, so it is adopted by now; the fallback only
                      // fires if the backend violated its terminal-event contract.
                      threadIdRef.current ?? '',
                      lastCall.toolCallId,
                      lastResult,
                      activeHost,
                      token,
                      handleEvent,
                      {
                        signal: abort.signal,
                        history: historyToSend,
                        emailContext: emailCtx,
                        documentContext: documentCtx,
                        surface: mailboxItem.surface,
                        latestDraft,
                        latestSearchResults,
                        // Keep attachments bound across a post-tool resume too, so an
                        // attachment write tool stays available if the graph re-enters
                        // it after a frontend pill (AIVA-6391).
                        attachments: attachmentsToSend,
                        confirmations: readConfirmations(),
                        userDisplayName: signedInDisplayName(),
                      }
                    ),
              currentToken,
              refreshTokens
            );
          }
        };
        await drainPendingToolCalls();
        // AIVA-8792 — v2 picked a Deckorate skill and ran nothing. Hand the turn
        // to the DeckorateChat endpoint (below, once this pass has fully closed).
        // Only from a v2 pass that is not itself a re-send, so it can never loop.
        handoffToDeckorate = shouldHandOffToDeckorate(turnState.handoffTarget, isDeckorate, opts.handoffOf);
        // AIVA-9184 — v2 routed "improve this slide" to the slide rewrite and ran nothing.
        handoffToRephrase = shouldHandOffToRephrase(turnState.handoffTarget, isDeckorate, opts.handoffOf);
      } catch (err) {
        handleStreamError(err, assistantMsgId, setMessages, userStoppedIdsRef.current.has(assistantMsgId));
        if (opts.deckSummary) toastDeckSummaryFailure(classifyChatError(err));
        turnFailed = true;
      } finally {
        completeTurnCapabilitySpan(
          capabilitySpanRef,
          turnState,
          turnFailed,
          userStoppedIdsRef.current.has(assistantMsgId)
        );
        userStoppedIdsRef.current.delete(assistantMsgId);
        untrackAbort();
        setStreaming(false);
        sendingRef.current = false;
        setActiveTool(null);
        setActivityLine(null);
        // Final scroll is also driven by the messages-change effect in
        // ChatMessages — once tool_end / pill emissions update the last
        // message, the effect re-fires and lands on the new bottom.
      }

      // AIVA-8792 — the Deckorate pass of a handed-off turn. Drop the empty v2
      // bubble, then run the same message on the Deckorate route as a hidden
      // turn: the user's bubble from this pass stays, so no second one is shown.
      if (handoffToDeckorate) {
        setMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
        await runChatTurnRef.current?.(text, {
          hidden: true,
          route: 'deckorate',
          handoffOf: { userMsgId, assistantMsgId },
        });
      }

      // AIVA-9184 — run the handed-off turn as the slide rewrite (SC-06). Drop
      // the empty v2 bubble; `hidden` because the user's bubble is already shown.
      if (handoffToRephrase) {
        setMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
        sendingRef.current = true;
        try {
          await runRephraseSlideTurn({
            text,
            hidden: true,
            setMessages,
            nextMsgId,
            apiToken: await resolveTurnBearer(ensureFreshToken, apiToken),
            refresh: refreshTokens ?? undefined,
            trigger: opts.capabilityTrigger,
            spans: slideRewriteSpansRef.current,
          });
        } finally {
          sendingRef.current = false;
        }
      }
    },
    [
      streaming,
      messages,
      apiToken,
      mailboxItem,
      wordDoc,
      resolveSendWordDoc,
      latestDraft,
      latestSearchResults,
      host,
      contextId,
      disclaimerText,
      refreshTokens,
      ensureFreshToken,
      personaDisplayName,
      aiProviderConnected,
      m365Connected,
      isDeckorateEnabled,
      makeTurnDeps,
      notePendingSend,
      resetThread,
      threadIdRef,
      trackAbort,
      selectedNameCandidates,
    ]
  );
  useEffect(() => {
    runChatTurnRef.current = runChatTurn;
  }, [runChatTurn]);

  /**
   * The plan card still asking a question, if the thread has one.
   *
   * Read from `messagesRef` rather than `messages` so the callers stay out of
   * the render-identity churn a `messages` dependency would cause.
   */
  const openMultipartPlan = useCallback((): { messageId: string; step: MultipartStep } | null => {
    for (let i = messagesRef.current.length - 1; i >= 0; i--) {
      const message = messagesRef.current[i];
      const plan = message?.multipartPlan;
      if (!plan) continue;
      // The newest card wins, answered or not — a settled one must not let an
      // older card upthread start capturing the user's typed words.
      if (plan.outcome !== undefined) return null;
      // Mid-run the card's buttons are disabled, but typing is not — and this
      // card's step is the one ALREADY executing until its turn completes, so
      // answering "yes" again here would run it a second time.
      if (plan.running === true) return null;
      const step = plan.steps[plan.cursor];
      return step ? { messageId: message!.id, step } : null;
    }
    return null;
  }, []);

  /**
   * Run, skip, or abandon the step one plan card is asking about.
   *
   * Each confirmed step is an ORDINARY chat turn: the step's self-contained
   * `subTask` is the message and its `skillKey` is pinned as `forced_skill_key`.
   * That pin does double duty — it stops the router re-classifying a step the
   * planner already decided, and it is what tells the backend to skip the
   * planner entirely, so a step can never be split into a plan of its own.
   *
   * The answered card is FROZEN in place with its outcome, and the next step's
   * question opens as a NEW card on the message the step's turn just produced —
   * so it sits directly below that step's answer. One card advancing in place
   * would have to relocate down the transcript on every step; anchoring a fresh
   * card per step means nothing ever moves.
   *
   * Both of those happen only once the turn has COMPLETED, which is what makes
   * the next question appear after the previous answer rather than during it.
   * They are in a `finally`, so a step that ERRORS still settles and the queue
   * moves on: stalling would strand the remaining steps behind a step that will
   * not succeed on a retry the card cannot offer, and the failure is visible in
   * the turn's own output directly above the next card.
   *
   * `skip` and `cancel` apply immediately — no turn runs for them, so there is
   * nothing to wait for and no new message to anchor to. Skip therefore opens
   * the next question on the SAME card rather than a new one; nothing was added
   * to the transcript, so nothing moves.
   */
  // SC-06 — Apply rewrites / Cancel. The card's telemetry span closes here.
  const handleSlideRewrite = useCallback(
    (messageId: string, card: SlideRewriteCard, decision: SlideRewriteDecision) =>
      decideSlideRewrite({ messageId, card, decision, setMessages, spans: slideRewriteSpansRef.current }),
    []
  );

  const handleMultipartStep = useCallback(
    async (messageId: string, action: 'run' | 'skip' | 'cancel', step?: MultipartStep): Promise<void> => {
      const patchPlan = (patch: (plan: MultipartPlan) => MultipartPlan) =>
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId && m.multipartPlan ? { ...m, multipartPlan: patch(m.multipartPlan) } : m
          )
        );

      if (action === 'cancel') {
        patchPlan((plan) => ({ ...plan, running: false, outcome: 'cancelled' }));
        return;
      }

      if (action === 'skip' || !step) {
        // No turn, so no new anchor: this card just moves on to the next step.
        // On the last step Skip is not offered, so there is always one to move to.
        patchPlan((plan) => ({
          ...plan,
          cursor: Math.min(plan.cursor + 1, plan.steps.length),
          running: false,
        }));
        return;
      }

      patchPlan((plan) => ({ ...plan, running: true }));
      try {
        await runChatTurn(step.subTask, {
          forcedSkillKey: step.skillKey,
          attachmentKnownPresent: true,
          // Without this the backend re-plans every replayed step (AIVA-8157).
          fromPlanStep: true,
        });
      } finally {
        setMessages((prev) => {
          const anchorIndex = prev.findIndex((m) => m.id === messageId);
          const anchor = anchorIndex >= 0 ? prev[anchorIndex] : undefined;
          const plan = anchor?.multipartPlan;
          if (!plan) return prev;

          const settled: ChatMessage = {
            ...anchor!,
            multipartPlan: { ...plan, running: false, outcome: 'ran' },
          };
          const next = [...prev];
          next[anchorIndex] = settled;

          const nextCursor = plan.cursor + 1;
          if (nextCursor >= plan.steps.length) return next;

          // Anchor the next question to the newest message the step produced.
          // If the turn added nothing (an error before any bubble), the newest
          // message IS this card's own anchor — carry on there rather than
          // dropping the rest of the queue on the floor.
          const targetIndex = next.length - 1;
          if (targetIndex <= anchorIndex) {
            next[anchorIndex] = {
              ...settled,
              multipartPlan: { ...plan, running: false, cursor: nextCursor },
            };
            return next;
          }
          next[targetIndex] = {
            ...next[targetIndex]!,
            // `compact`: a follow-up asks about its own step only. The opening
            // card already laid out the plan, and repeating it here reads as
            // the original combined request being asked all over again.
            multipartPlan: { steps: plan.steps, cursor: nextCursor, compact: true },
          };
          return next;
        });
      }
    },
    [runChatTurn]
  );

  const handleSend = useCallback(
    async (e: FormEvent, attachedContent?: string, attachedFileName?: string) => {
      e.preventDefault();
      const text = input.trim();
      const filesToSend = attachedFiles.slice();
      if (!text && !attachedContent && filesToSend.length === 0) return;
      // Any candidate resolved by find_user_by_name and still selected rides
      // along as hidden metadata — appended to what's actually SENT, never
      // to what the bubble shows (`displayText` below stays the clean typed
      // sentence). Same split the codebase already uses for a slash
      // command's expanded prompt or an attachment's extracted text.
      if (skillThreadPendingRef.current && !(await skillThreadPendingRef.current)) return;
      // In a skill message's session, the first reply also carries hidden context
      // naming the message: the model's memory does not contain the seed bubble.
      const session = skillSessionRef.current;
      const skillContextSuffix =
        session && !messagesRef.current.some((m) => m.role === 'user') ? skillMessageContextSuffix(session) : '';
      const resolvedContactsSuffix =
        buildResolvedContactsSuffix([...selectedNameCandidates.values()]) + skillContextSuffix;
      setInput('');
      setAttachedFiles([]);

      // A plan card asks a yes/no question, and plenty of users answer a
      // question by typing rather than by clicking the button next to it. Route
      // a bare "yes" / "no" to the card so both ways of answering behave
      // identically.
      //
      // Without this the typed answer becomes an ordinary turn, and the router
      // reads a short reply after a question as a CONTINUATION — so "yes" would
      // re-run whatever skill spoke last instead of starting the step being
      // offered. See the continuation rule in skill_router's _ROUTER_OPENING.
      //
      // Deliberately strict: only a bare affirmative with nothing else in it.
      // "yes, but do the alignment first" carries an instruction the card
      // cannot honour, so it stays an ordinary turn where the model can read
      // it. Skipped entirely when a file or attachment rides along, since that
      // is plainly not an answer to "shall I do this now?".
      const planAnswer =
        attachedContent == null && filesToSend.length === 0 ? affirmativeOrNegative(text) : null;
      const openPlan = planAnswer ? openMultipartPlan() : null;
      if (planAnswer && openPlan) {
        await handleMultipartStep(openPlan.messageId, planAnswer === 'yes' ? 'run' : 'cancel', openPlan.step);
        return;
      }

      await withCorrelationId(async () => {
        logEvent('chat.message.sent', 'usage', {
          characterCount: text.length,
          hasAttachment: attachedContent != null || filesToSend.length > 0,
        });
        const started = performance.now();
        try {
          await sendChatMessage(runChatTurn, {
            text,
            resolvedContactsSuffix,
            attachedContent,
            attachedFileName,
            filesToSend,
            capabilityTrigger: 'natural_language',
          });
        } finally {
          logEvent('chat.response.received', 'performance', {
            durationMs: Math.round(performance.now() - started),
          });
        }
      });
    },
    [input, attachedFiles, runChatTurn, selectedNameCandidates, handleMultipartStep, openMultipartPlan]
  );

  // Pill click dispatcher.  office_action pills run a known Office.js
  // handler inline (no backend round-trip).  chat_intent pills reuse the
  // chat-turn path so the LLM sees them as a follow-up user message; the
  // pill's `message` is constructed by the LLM at emit time and must be
  // self-contained (it survives long histories independently of context).
  const handlePillBooked = useCallback(
    (messageId: string, pillLabel: string) => {
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, bookedPillLabel: pillLabel } : m)));
    },
    [setMessages]
  );

  const handlePillClick = useCallback(
    async (pill: Pill) => {
      // A pill click submits a new turn — clear the input so the
      // slash-command palette (driven by input.startsWith("/")) closes,
      // matching what the Send button does. AIVA-5058.

      // A candidate-selection pill (e.g. picking from several same-first-name
      // directory matches) isn't a submit at all — the user hasn't said what to
      // DO with the name(s) yet. It's also multi-select: clicking toggles this
      // candidate in or out of the set, and the input is rebuilt from the FULL
      // current selection (not just this click) so a second pick adds "and
      // Jawaharlal Nehru E" rather than replacing the first name. Handled
      // before the unconditional clear below, which every other pill type
      // wants (a real submit) and which would otherwise wipe whatever the
      // user had already typed around the name list.
      if (pill.type === 'name_candidate') {
        const { text, selected } = applyNameCandidateClick(
          pill,
          selectedNameCandidates,
          input,
          nameListAnchorRef,
          lastJoinedNameTextRef
        );
        setInput(text);
        setSelectedNameCandidates(selected);
        return;
      }

      setInput('');
      if (pill.type === 'office_action') {
        await dispatchOfficeActionPill(
          pill,
          host,
          setMessages,
          nextMsgId,
          openedDraftIdsRef.current,
          { apiToken, refresh: refreshTokens ?? undefined }
        );
      } else {
        // A quick reply tapped while its message's thread is still being created.
        if (skillThreadPendingRef.current && !(await skillThreadPendingRef.current)) return;
        // AIVA-7482 — remember the slot BEFORE the turn runs, so it rides this turn
        // and every one after until the booking resolves. The backend parses the same
        // click out of this turn's message, but that parse only ever sees the CURRENT
        // turn: the moment anything costs a turn in between — the external-recipient
        // question is the first thing that does — the evidence is gone and the booking
        // is refused for a slot the user plainly chose.
        const clickedSlot = parseBookSlotConfirmation(pill.message);
        if (clickedSlot) {
          pendingConfirmationsRef.current = { ...pendingConfirmationsRef.current, bookingSlot: clickedSlot };
          confirmationsSetAtRef.current = Date.now();
        }
        // AIVA-5166: book-slot pill messages carry technical scheduling params;
        // show the pill's clean label in the bubble, not the raw message (which
        // still goes to the backend verbatim).
        await runChatTurn(pill.message, {
          hidden: pill.hidden,
          displayText: pill.bubbleText ?? bookSlotBubbleOverride(pill.message, pill.label),
          // Pins the turn to a known skill where the pill declares one (Book
          // meeting → calendar_assistant), so the click cannot be mis-routed.
          ...(pill.forcedSkillKey ? { forcedSkillKey: pill.forcedSkillKey } : {}),
          capabilityTrigger: 'action_button',
        });
        // Specification §6.2: clicking a suggested slot drafts the invite —
        // "Toast: 'Review and send your meeting invite'". Emitted after the turn
        // settles so the invite it produces actually exists when we point the
        // user at it. Only the slot pills carry booksSlot.
        if (pill.booksSlot) {
          emitToast({ title: 'Review and send your meeting invite', intent: 'info' });
        }
      }
    },
    [host, runChatTurn, selectedNameCandidates, input]
  );

  // AIVA-6436 — the user amended a Word write-pill approval card. Send the
  // feedback as a follow-up turn so the LLM regenerates its suggestion; the
  // bubble shows just their words, not the wrapper prompt.
  const handlePillAmend = useCallback(
    async (_pill: Pill, feedback: string): Promise<void> => {
      const trimmed = feedback.trim();
      if (!trimmed) return;
      const prompt = `Please revise your previous suggestion. Feedback from the user: ${trimmed}`;
      await runChatTurn(prompt, { displayText: trimmed });
    },
    [runChatTurn]
  );

  /**
   * The pending confirmations, or `null` once they have gone stale.
   *
   * Specification gap G-10 is why this expires: the prototype's
   * `pendingBookingContext` was cleared only on resolution, so a booking the user
   * had walked away from could silently resume turns later with the old topic and
   * attendee. A carried slot has exactly that failure mode.
   *
   * Bounded by time rather than by turns, because surviving an intervening turn is
   * the entire point here — the external-recipient question costs one.
   */
  const readConfirmations = useCallback((): TurnConfirmations | undefined => {
    const pending = pendingConfirmationsRef.current;
    if (!pending) return undefined;
    if (Date.now() - confirmationsSetAtRef.current > CONFIRMATION_TTL_MS) {
      pendingConfirmationsRef.current = null;
      return undefined;
    }
    return pending;
  }, []);

  /**
   * Remember external recipients the user has approved, so they are not re-asked.
   *
   * Merged and deduped case-insensitively: a thread can approve one group and then
   * add someone, and the earlier approvals must survive — otherwise the follow-up
   * turn re-asks about everyone. The backend then names only the people NOT in this
   * list, which is what closes the ticket's "repeated confirmations" report.
   */
  const recordConfirmedExternals = useCallback((addresses: string[]) => {
    const existing = pendingConfirmationsRef.current?.externalRecipients ?? [];
    const seen = new Set(existing.map((a) => a.toLowerCase()));
    const merged = [...existing];
    for (const a of addresses) {
      const key = a.trim().toLowerCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        merged.push(a.trim());
      }
    }
    if (merged.length === existing.length) return;
    pendingConfirmationsRef.current = { ...pendingConfirmationsRef.current, externalRecipients: merged };
    confirmationsSetAtRef.current = Date.now();
  }, []);

  /** Drop carried confirmations — a new thread, or a resolved booking. */
  const clearConfirmations = useCallback(() => {
    pendingConfirmationsRef.current = null;
    confirmationsSetAtRef.current = 0;
  }, []);

  const clearDraft = useCallback(() => setLatestDraft(null), []);

  const addAttachedFiles = useCallback((files: File[]) => {
    setAttachedFiles((prev) => [...prev, ...files]);
  }, []);

  const removeAttachedFile = useCallback((index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * Reset everything the pane holds for the conversation on screen, without
   * touching the persisted copy or the server thread. Shared by New chat (which
   * also deletes the persisted copy) and by switching to or from a skill
   * message's session (which must keep the conversation it leaves).
   */
  const resetConversationState = useCallback(() => {
    deckorateThreadIdRef.current = null;
    // docReviewReadyRef must be cleared in lockstep with the thread id it
    // was set for (AIVA-7506 review) — otherwise ChatMessages renders
    // DocReviewPanel with docReviewReady=true against the new (null/fresh)
    // threadIdRef, a stale pairing left over from the discarded thread.
    docReviewReadyRef.current = false;
    msgCounterRef.current = 0;
    sendingRef.current = false;
    pillsEmittedRef.current = false;
    lastWordPillTextRef.current = '';
    composeDraftIdRef.current = '';
    replyTargetConversationIdRef.current = null;
    latestDraftModeRef.current = null;
    setMessages([]);
    setInput('');
    setStreaming(false);
    setActiveTool(null);
    setActivityLine(null);
    setLatestDraft(null);
    setLatestSearchResults([]);
    setAttachedFiles([]);
    // Drop the accumulated session files too, otherwise "New Chat" would
    // silently carry the previous conversation's uploads into the fresh one.
    sessionUploadedFilesRef.current = [];
    setSessionUploadedFileCount(0);
    setSessionUploadedFileBytes(0);
    openedDraftIdsRef.current = new Set();
    // Same reasoning as the session files above: a slot picked in the old
    // conversation, or recipients approved there, must not apply to the new one.
    clearConfirmations();
  }, [clearConfirmations]);

  const endSkillSession = useCallback(() => {
    // A thread still being created for the session belongs to nothing now.
    skillOpenEpochRef.current += 1;
    previousConversationRef.current = null;
    setSkillSession(null);
    saveSkillSession(host, contextId, null);
  }, [host, contextId]);

  const clearChat = useCallback(() => {
    abortAllStreams();
    // AIVA-5171: drop the persisted copy too so "New Chat" doesn't resurrect
    // the old conversation on the next panel reopen. AIVA-5796: clear only the
    // current document's bucket (Word/PowerPoint), not every document's.
    clearChatHistory(host, contextId);
    setChatCleared(host, contextId, true);
    // Null the server thread (creation stays lazy — the next send starts a
    // fresh one). Also invalidates any in-flight hydration via the epoch bump.
    resetThread();
    resetConversationState();
    endSkillSession();
  }, [host, contextId, resetThread, resetConversationState, endSkillSession]);

  const startNewChat = clearChat;

  /** Server messages of a thread, mapped for rendering (as hydration does). */
  const fetchThreadForRender = useCallback(
    async (threadId: string): Promise<ChatMessage[]> => {
      const dtos = await getThreadMessages(apiToken, threadId, refreshTokens ?? undefined);
      return resolveMultipartCursors(
        dtos.map((dto) => mapThreadMessage(dto, { host, surface: mailboxItem.surface ?? null }))
      );
    },
    [apiToken, refreshTokens, host, mailboxItem.surface]
  );

  /** Put a conversation on screen in place of the current one, keeping both on the server. */
  const showConversation = useCallback(
    (threadId: string | null, transcript: ChatMessage[], msgCounter = 0) => {
      abortAllStreams();
      // Bumps the hydration epoch too, so a slow snapshot can't land on top.
      resetThread();
      resetConversationState();
      threadIdRef.current = threadId;
      msgCounterRef.current = msgCounter;
      setMessages(transcript);
      setChatCleared(host, contextId, false);
      // A send right after switching must not read the open email, or the
      // current Word selection, as "new" and start yet another thread (see
      // resolveThreadReset). The selection key is re-recorded by that send.
      lastEmailIdRef.current = mailboxItem.itemId ?? lastEmailIdRef.current;
      lastSelectionKeyRef.current = null;
    },
    [host, contextId, resetThread, resetConversationState, threadIdRef, mailboxItem.itemId]
  );

  /** Leave a skill message's session and put the previous conversation back. */
  const returnToPreviousChat = useCallback(async () => {
    const previous = previousConversationRef.current;
    const info = skillSessionRef.current;
    endSkillSession();
    if (previous) {
      showConversation(previous.threadId, previous.messages, previous.msgCounter);
      return;
    }
    const threadId = info?.previousThreadId ?? null;
    if (!threadId) {
      showConversation(null, []);
      return;
    }
    try {
      showConversation(threadId, await fetchThreadForRender(threadId));
    } catch {
      // The earlier conversation expired: open an empty chat instead.
      showConversation(null, []);
    }
  }, [endSkillSession, showConversation, fetchThreadForRender]);

  /**
   * Open a skill message as its own chat session: the message is the first
   * assistant bubble and its quick replies are pills. Re-opening the same
   * message returns to the session it was first opened in. The conversation
   * that was on screen is kept and can be restored with `returnToPreviousChat`.
   * The message shows at once; its thread is found or created in the
   * background, and a send made meanwhile waits for it.
   */
  const openSkillMessage = useCallback(
    (message: SkillMessage) => {
      if (!host) return;
      if (skillSessionRef.current?.notificationId === message.notificationId) return;
      const epoch = ++skillOpenEpochRef.current;
      // Keep the ORIGINAL conversation when hopping from one message to another.
      if (!skillSessionRef.current) {
        previousConversationRef.current = {
          threadId: threadIdRef.current,
          messages: messagesRef.current,
          msgCounter: msgCounterRef.current,
        };
      }
      const previousThreadId =
        skillSessionRef.current?.previousThreadId ?? previousConversationRef.current?.threadId ?? null;
      // The message is drawn at once from the notification in hand, mapped the
      // way the server's stored copy of the seed is, so the two render the same.
      const seed = buildSkillMessageSeed(message);
      const seedBubble = mapThreadMessage(
        {
          id: `seed-${message.notificationId}`,
          role: 'assistant',
          content: seed.content,
          pills: seed.pills,
          created_at: new Date().toISOString(),
        },
        { host, surface: mailboxItem.surface ?? null }
      );
      showConversation(null, [seedBubble]);
      const pendingInfo: SkillSessionInfo = {
        notificationId: message.notificationId,
        threadId: null,
        skillName: message.skillName,
        title: message.title,
        body: message.body,
        receivedAt: message.createdAtUtc,
        previousThreadId,
      };
      skillSessionRef.current = pendingInfo;
      setSkillSession(pendingInfo);

      const ready = (async (): Promise<boolean> => {
        try {
          let threadId = lookupSkillMessageThread(message.notificationId);
          let transcript: ChatMessage[] | null = null;
          if (threadId) {
            try {
              transcript = await fetchThreadForRender(threadId);
            } catch (err) {
              // The session expired with the 3-day thread TTL: start a new one.
              if (!(err instanceof ThreadNotFoundError)) throw err;
              forgetSkillMessageThread(message.notificationId);
              threadId = null;
            }
          }
          if (!threadId) {
            threadId = await createThread(apiToken, host, contextId, refreshTokens ?? undefined, seed);
            rememberSkillMessageThread(message.notificationId, threadId);
          }
          if (epoch !== skillOpenEpochRef.current) return false;
          threadIdRef.current = threadId;
          // A reopened session can hold replies made since the message. Nothing
          // was sent here in the meantime: sends wait on this promise.
          if (transcript) setMessages(transcript);
          const info: SkillSessionInfo = { ...pendingInfo, threadId };
          skillSessionRef.current = info;
          setSkillSession(info);
          saveSkillSession(host, contextId, info);
          return true;
        } catch {
          if (epoch !== skillOpenEpochRef.current) return false;
          emitToast({ title: "Couldn't open the message. Try again.", intent: 'error' });
          void returnToPreviousChat();
          return false;
        }
      })();
      skillThreadPendingRef.current = ready;
      void ready.finally(() => {
        if (skillThreadPendingRef.current === ready) skillThreadPendingRef.current = null;
      });
    },
    [
      host,
      contextId,
      apiToken,
      refreshTokens,
      mailboxItem.surface,
      fetchThreadForRender,
      showConversation,
      returnToPreviousChat,
      threadIdRef,
    ]
  );

  const stopStreaming = useCallback(() => {
    const msgs = messagesRef.current;
    let target: ChatMessage | undefined;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i]?.role === 'assistant') {
        target = msgs[i];
        break;
      }
    }
    if (target) userStoppedIdsRef.current.add(target.id);
    abortAllStreams();
    if (target) {
      const stoppedId = target.id;
      const pendingApproval = target.approval;
      setMessages((prev) => prev.map((m) => (m.id === stoppedId ? { ...m, stopped: true } : m)));
      // AIVA-7912: Stop only cancelled the client-side stream — the interrupt
      // this card represents stayed parked server-side, so a decided-looking
      // "Stopped by you" card was still live underneath and could resurface on
      // rehydration. Reject it through the same path Deny uses so the backend
      // interrupt actually closes instead of dangling.
      if (pendingApproval && pendingApproval.decision === undefined) {
        void handleApprovalDecision(stoppedId, pendingApproval, 'reject');
      }
    }
  }, [abortAllStreams, handleApprovalDecision]);

  const regenerate = useCallback(
    (assistantMessageId: string) => {
      const idx = messages.findIndex((m) => m.id === assistantMessageId);
      if (idx < 0) return;
      let userText: string | undefined;
      for (let i = idx - 1; i >= 0; i--) {
        if (messages[i]?.role === 'user') {
          userText = messages[i]?.content;
          break;
        }
      }
      if (!userText) return;
      // Forward the skill this message was originally produced by so
      // runChatTurn's selection/M365 guards re-run against the document's
      // CURRENT state, instead of silently sending plain text (AIVA-7079) —
      // e.g. the user deselected before hitting Regenerate on a stopped,
      // selection-required turn.
      // Replay the selection the stopped turn ran with. Without this the guard
      // reads the live selection, which the Regenerate click itself has usually
      // just cleared, and nudges the user to re-select text for a reply already
      // part-way on screen (AIVA-7571). Absent on a turn that genuinely had no
      // selection, so AIVA-7079's deselected-retry guard still fires there.
      void runChatTurn(userText, {
        hidden: true,
        forcedSkillKey: messages[idx]?.skillKey,
        replaySelection: messages[idx]?.sentSelection,
      });
    },
    [messages, runChatTurn]
  );

  const runSlashCommand = useCallback(
    async (cmd: SlashCommand) => {
      // skill.invoke fires whether or not skillKey is set — a slash command
      // without a forced skill still maps to one server-side via routing.
      logEvent('skill.invoke', 'usage', {
        skillId: cmd.skillKey || cmd.slash.replace(/^\//, ''),
      });
      const started = performance.now();
      try {
        await runChatTurn(cmd.prompt, {
          autoApply: cmd.autoApply,
          forcedSkillKey: cmd.skillKey || undefined,
          capabilityTrigger: 'palette',
          // Commands that opt into ``echoSlashInBubble`` show the slash in the
          // user bubble instead of ``cmd.prompt``. The palette path passes a
          // forcedSkillKey, which is selectUserBubbleContent's signal that the
          // caller already supplied friendly text (AIVA-5057) — true for
          // /meeting, wrong for a no-arg slash whose "friendly text" is just
          // its own menu description echoed back at the user.
          ...(cmd.echoSlashInBubble ? { displayText: cmd.slash } : {}),
        });
      } finally {
        logEvent('perf.skill.execute', 'performance', {
          skillId: cmd.skillKey || cmd.slash.replace(/^\//, ''),
          durationMs: Math.round(performance.now() - started),
        });
      }
    },
    [runChatTurn]
  );

  // ST-05 (AIVA-6805): a PowerPoint "All actions" entry sent as if typed, so
  // the local DI-01/DI-02/DI-04 handlers answer it exactly as they would the
  // same words in the composer.
  const sendPrompt = useCallback(
    async (text: string) => {
      await runChatTurn(text, { capabilityTrigger: 'palette' });
    },
    [runChatTurn]
  );

  // Just the ids, for ActionPills to check membership against — the name
  // text each one contributes is only needed inside handlePillClick above.
  const selectedNameCandidateIds = useMemo(
    () => new Set(selectedNameCandidates.keys()),
    [selectedNameCandidates]
  );

  return {
    messages,
    input,
    setInput,
    streaming,
    activeTool,
    activityLine,
    docReviewScope,
    handleApprovalDecision,
    pendingDraft: latestDraft,
    clearDraft,
    clearChat,
    chatEndRef,
    startNewChat,
    stopStreaming,
    regenerate,
    handleSend,
    handlePillClick,
    handlePillAmend,
    handleMultipartStep,
    handleSlideRewrite,
    handlePillBooked,
    selectedNameCandidateIds,
    runSlashCommand,
    sendPrompt,
    attachedFiles,
    addAttachedFiles,
    removeAttachedFile,
    sessionUploadedFileCount,
    sessionUploadedFileBytes,
    threadIdRef,
    docReviewReadyRef,
    skillSession,
    openSkillMessage,
    returnToPreviousChat,
  };
}
